<template>
  <!-- Lab Order Action Banner -->
  <div v-if="shouldShowLabOrderBanner" class="my-4">
    <div class="bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-400 p-4 rounded-r-lg shadow-sm transition-opacity duration-200"
         :class="{ 'opacity-30': newSamples.length > 0 || isAddingNewSample }">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-orange-800">
              {{ labOrderBannerTitle }}
            </h3>
            <div class="mt-1 text-sm text-orange-700">
              {{ labOrderBannerMessage }}
            </div>
          </div>
        </div>
        <div class="flex items-center space-x-3">
          <button
            @click="sendToLab"
            :disabled="isProcessingLabOrder"
            class="px-4 py-2 bg-orange-600 border border-transparent rounded-md font-semibold text-sm text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition ease-in-out duration-150"
          >
            <span v-if="isProcessingLabOrder" class="flex items-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
            <span v-else>{{ labOrderBannerAction }}</span>
          </button>
          <button
            @click="dismissLabOrderBanner"
            class="text-orange-400 hover:text-orange-600 focus:outline-none"
            title="Dismiss this reminder"
          >
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="samples.length > 0 || newSamples.length > 0" class="bg-white px-6 py-4 my-4">
    <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-4">
      <div class="flex flex-wrap items-center gap-2 min-w-0">
        <!-- Column Visibility Controls -->
        <div class="relative" v-if="samples.length > 0 || newSamples.length > 0">
          <button @click="showColumnControls = !showColumnControls" class="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500">
            <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
            </svg>
            Columns ({{ visibleColumnCount }}/{{ totalColumnCount }})
          </button>
          
          <div v-if="showColumnControls" class="absolute left-0 mt-2 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50" @click.stop>
            <div class="p-3">
              <div class="flex items-center justify-between mb-3">
                <h4 class="text-sm font-medium text-gray-900">Show/Hide Columns</h4>
                <button @click="showColumnControls = false" class="text-gray-400 hover:text-gray-600">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              
              <!-- Quick Actions -->
              <div class="flex space-x-1 mb-3">
                <button @click="showAllColumns" class="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                  Show All
                </button>
                <button @click="hideAllColumns" class="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                  Hide All
                </button>
                <button @click="resetColumnsToDefault" class="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                  Reset
                </button>
              </div>
              
              <!-- Column Checkboxes -->
              <div class="space-y-2 max-h-60 overflow-y-auto">
                <label class="flex items-center cursor-pointer" @click="toggleColumn('sampleId')">
                  <input type="checkbox" v-model="columnVisibility.sampleId" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">Sample ID</span>
                </label>
                <label class="flex items-center cursor-pointer" @click="toggleColumn('reference')">
                  <input type="checkbox" v-model="columnVisibility.reference" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">Reference</span>
                </label>
                <label class="flex items-center cursor-pointer" @click="toggleColumn('soilType')">
                  <input type="checkbox" v-model="columnVisibility.soilType" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">Soil Type</span>
                </label>
                <label class="flex items-center cursor-pointer" @click="toggleColumn('assigned')">
                  <input type="checkbox" v-model="columnVisibility.assigned" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">Assigned</span>
                </label>
                <label class="flex items-center cursor-pointer" @click="toggleColumn('farmName')">
                  <input type="checkbox" v-model="columnVisibility.farmName" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">Farm Name</span>
                </label>
                <label class="flex items-center cursor-pointer" @click="toggleColumn('parcelNo')">
                  <input type="checkbox" v-model="columnVisibility.parcelNo" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">Parcel No</span>
                </label>
                <label class="flex items-center cursor-pointer" @click="toggleColumn('parcelName')">
                  <input type="checkbox" v-model="columnVisibility.parcelName" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">Parcel Name</span>
                </label>
                <label class="flex items-center cursor-pointer" @click="toggleColumn('test')">
                  <input type="checkbox" v-model="columnVisibility.test" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">Test</span>
                </label>
                <label class="flex items-center cursor-pointer" @click="toggleColumn('comment')">
                  <input type="checkbox" v-model="columnVisibility.comment" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">Comment</span>
                </label>
                <label class="flex items-center cursor-pointer" @click="toggleColumn('lpis')">
                  <input type="checkbox" v-model="columnVisibility.lpis" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">LPIS</span>
                </label>
                <label class="flex items-center cursor-pointer" @click="toggleColumn('collected')">
                  <input type="checkbox" v-model="columnVisibility.collected" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">Collected</span>
                </label>
                <label class="flex items-center cursor-pointer" @click="toggleColumn('georef')">
                  <input type="checkbox" v-model="columnVisibility.georef" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">Georef.</span>
                </label>
                <label class="flex items-center cursor-pointer" @click="toggleColumn('result')">
                  <input type="checkbox" v-model="columnVisibility.result" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                  <span class="ml-2 text-sm text-gray-700">Result</span>
                </label>
                
                <!-- Lab Results Columns -->
                <div class="border-t mt-2 pt-2">
                  <div class="text-xs text-gray-500 mb-2 font-medium">Lab Results</div>
                  
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labPh')">
                    <input type="checkbox" v-model="columnVisibility.labPh" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">pH</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labPhosphorus')">
                    <input type="checkbox" v-model="columnVisibility.labPhosphorus" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">Phosphorus</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labPIndexGrassland')">
                    <input type="checkbox" v-model="columnVisibility.labPIndexGrassland" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">P Index (Grassland)</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labPIndexOther')">
                    <input type="checkbox" v-model="columnVisibility.labPIndexOther" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">P Index (Other)</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labPotassium')">
                    <input type="checkbox" v-model="columnVisibility.labPotassium" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">Potassium</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labKIndexMineral')">
                    <input type="checkbox" v-model="columnVisibility.labKIndexMineral" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">K Index (Mineral)</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labKIndexPeat')">
                    <input type="checkbox" v-model="columnVisibility.labKIndexPeat" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">K Index (Peat)</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labMagnesium')">
                    <input type="checkbox" v-model="columnVisibility.labMagnesium" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">Magnesium</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labCalcium')">
                    <input type="checkbox" v-model="columnVisibility.labCalcium" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">Calcium</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labCopper')">
                    <input type="checkbox" v-model="columnVisibility.labCopper" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">Copper</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labManganese')">
                    <input type="checkbox" v-model="columnVisibility.labManganese" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">Manganese</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labZinc')">
                    <input type="checkbox" v-model="columnVisibility.labZinc" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">Zinc</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labBoron')">
                    <input type="checkbox" v-model="columnVisibility.labBoron" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">Boron</span>
                  </label>
                  
                  <!-- Micronutrient Status Columns -->
                  <div class="border-t mt-2 pt-2">
                    <div class="text-xs text-gray-500 mb-2 font-medium">Micronutrient Status</div>
                    
                    <label class="flex items-center cursor-pointer" @click="toggleColumn('labMagnesiumStatus')">
                      <input type="checkbox" v-model="columnVisibility.labMagnesiumStatus" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                      <span class="ml-2 text-sm text-gray-700">Mg Status</span>
                    </label>
                    <label class="flex items-center cursor-pointer" @click="toggleColumn('labCalciumStatus')">
                      <input type="checkbox" v-model="columnVisibility.labCalciumStatus" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                      <span class="ml-2 text-sm text-gray-700">Ca Status</span>
                    </label>
                    <label class="flex items-center cursor-pointer" @click="toggleColumn('labCopperStatus')">
                      <input type="checkbox" v-model="columnVisibility.labCopperStatus" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                      <span class="ml-2 text-sm text-gray-700">Cu Status</span>
                    </label>
                    <label class="flex items-center cursor-pointer" @click="toggleColumn('labManganeseStatus')">
                      <input type="checkbox" v-model="columnVisibility.labManganeseStatus" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                      <span class="ml-2 text-sm text-gray-700">Mn Status</span>
                    </label>
                    <label class="flex items-center cursor-pointer" @click="toggleColumn('labZincStatus')">
                      <input type="checkbox" v-model="columnVisibility.labZincStatus" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                      <span class="ml-2 text-sm text-gray-700">Zn Status</span>
                    </label>
                    <label class="flex items-center cursor-pointer" @click="toggleColumn('labBoronStatus')">
                      <input type="checkbox" v-model="columnVisibility.labBoronStatus" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                      <span class="ml-2 text-sm text-gray-700">B Status</span>
                    </label>
                  </div>
                  
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labOm')">
                    <input type="checkbox" v-model="columnVisibility.labOm" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">Organic Matter</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labLimeReq')">
                    <input type="checkbox" v-model="columnVisibility.labLimeReq" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">Lime Req.</span>
                  </label>
                  <label class="flex items-center cursor-pointer" @click="toggleColumn('labTestDate')">
                    <input type="checkbox" v-model="columnVisibility.labTestDate" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <span class="ml-2 text-sm text-gray-700">Test Date</span>
                  </label>
                </div>

              </div>
            </div>
          </div>
        </div>
        
        <!-- Sample ID Search -->
        <div class="relative" v-if="samples.length > 0 || newSamples.length > 0">
          <div class="relative inline-flex items-center h-10 rounded-md border bg-white border-gray-300 text-gray-600 hover:bg-gray-50 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500 transition-colors pl-3 pr-2 w-48 max-w-full">
            <!-- leading search icon -->
            <svg
              v-if="!sampleIdSearch || sampleIdSearch.length === 0"
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 text-gray-500 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <!-- input -->
            <input
              type="text"
              autocomplete="off"
              placeholder="Search Sample ID"
              v-model="sampleIdSearch"
              @input="handleSampleIdSearch"
              class="bg-transparent border-0 outline-none focus:outline-none focus:ring-0 text-xs placeholder-gray-400 text-gray-700 w-full pr-4"
            />
            <!-- clear button -->
            <button
              v-if="sampleIdSearch && sampleIdSearch.length > 0"
              type="button"
              @click="sampleIdSearch = ''; handleSampleIdSearch();"
              aria-label="Clear search"
              class="ml-1 h-5 w-5 inline-flex items-center justify-center rounded-full text-gray-500 hover:text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-400"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm2.28-10.53a.75.75 0 10-1.06-1.06L10 7.94 8.78 6.72a.75.75 0 10-1.06 1.06L8.94 9l-1.22 1.22a.75.75 0 101.06 1.06L10 10.06l1.22 1.22a.75.75 0 101.06-1.06L11.06 9l1.22-1.22z" clip-rule="evenodd"/></svg>
            </button>
          </div>
        </div>

        <!-- Farm Filter -->
        <div class="relative" v-if="samples.length > 0 || newSamples.length > 0">
          <div class="w-64 max-w-full">
            <!-- Farm filter button -->
            <button 
              @click="showFarmFilterDropdown = !showFarmFilterDropdown"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-left flex items-center justify-between"
            >
              <div class="flex-1 min-w-0">
                <div v-if="farmFilter.length === 0" class="text-gray-500">
                  Filter by Farm
                </div>
                <div v-else class="flex flex-wrap gap-1">
                  <span 
                    v-for="farmName in farmFilter" 
                    :key="farmName"
                    class="inline-flex items-center px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded-full"
                  >
                    {{ farmName === '__UNASSIGNED__' ? 'Unassigned' : farmName }}
                    <button 
                      @click.stop="removeFarmFilter(farmName)"
                      class="ml-1 text-indigo-600 hover:text-indigo-800 focus:outline-none"
                    >
                      ×
                    </button>
                  </span>
                </div>
              </div>
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>

            <!-- Farm filter dropdown -->
            <div 
              v-if="showFarmFilterDropdown" 
              class="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
            >
              <div v-if="availableFarms.length === 0" class="px-3 py-2 text-sm text-gray-500">
                No farms available
              </div>
              <label 
                v-for="farm in availableFarms" 
                :key="farm.value"
                class="flex items-center px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
              >
                <input 
                  type="checkbox" 
                  :value="farm.value"
                  v-model="farmFilter"
                  class="mr-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span class="truncate">{{ farm.label }}</span>
              </label>
              
              <!-- Clear all option -->
              <div v-if="farmFilter.length > 0" class="border-t border-gray-200">
                <button 
                  @click="clearAllFarmFilters"
                  class="w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-2 min-w-0">
        <!-- View Presets -->
        <div class="flex flex-wrap items-center gap-2 min-w-0" v-if="samples.length > 0">
          <!-- Data Type Toggle -->
          <div class="flex items-center border border-gray-300 rounded-md overflow-hidden">
            <button 
              @click="setDataType('overview')"
              :class="[
                'px-3 py-2 text-sm font-medium transition-colors duration-150 border-r border-gray-300',
                dataType === 'overview' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'
              ]"
              title="Show essential sample information columns"
            >
              Task View
            </button>
            <button 
              @click="setDataType('major')"
              :class="[
                'px-3 py-2 text-sm font-medium transition-colors duration-150 border-r border-gray-300',
                dataType === 'major' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'
              ]"
              title="Show major nutrients (pH, P, K, Lime)"
            >
              Major Nutrients
            </button>
            <button 
              @click="setDataType('micro')"
              :class="[
                'px-3 py-2 text-sm font-medium transition-colors duration-150 border-r border-gray-300',
                dataType === 'micro' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'
              ]"
              title="Show micronutrients (Mg, Ca, Cu, Mn, Zn, B)"
            >
              Micronutrients
            </button>
            <button 
              @click="setDataType('om')"
              :class="[
                'px-3 py-2 text-sm font-medium transition-colors duration-150',
                dataType === 'om' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'
              ]"
              title="Show organic matter values"
            >
              OM
            </button>
          </div>
          
          <!-- View Type Toggle (always visible, disabled for overview) -->
          <div class="flex items-center border border-gray-300 rounded-md overflow-hidden">
            <button 
              @click="dataType !== 'overview' && dataType !== 'om' ? setViewType('interpreted') : null"
              :class="[
                'px-3 py-2 text-sm font-medium transition-colors duration-150 border-r border-gray-300',
                dataType === 'overview' || dataType === 'om' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' :
                viewType === 'interpreted' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'
              ]"
              :disabled="dataType === 'overview' || dataType === 'om'"
              :title="dataType === 'overview' ? 'Not available for overview' : dataType === 'om' ? 'Not available for organic matter' : (dataType === 'major' ? 'Show P and K indices' : 'Show micronutrient status (VL/L/N/H/VH)')"
            >
              Index
            </button>
            <button 
              @click="dataType !== 'overview' && dataType !== 'om' ? setViewType('values') : null"
              :class="[
                'px-3 py-2 text-sm font-medium transition-colors duration-150',
                dataType === 'overview' || dataType === 'om' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' :
                viewType === 'values' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'
              ]"
              :disabled="dataType === 'overview' || dataType === 'om'"
              :title="dataType === 'overview' ? 'Not available for overview' : dataType === 'om' ? 'Not available for organic matter' : 'Show raw measurement values (mg/l)'"
            >
              Values
            </button>
          </div>
        </div>
        
        <!-- Actions Menu -->
        <div class="relative">
          <button @click="showActionsMenu = !showActionsMenu" class="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500">
            <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path>
            </svg>
            
          </button>
          <div v-if="showActionsMenu" class="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-[999999]">
            <div class="py-1">
              <!-- Sort Samples Section -->
              <div v-if="samples.length > 0">
                <button @click="showSortSubmenu = !showSortSubmenu" class="flex items-center justify-between w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" :class="{ 'bg-gray-50': showSortSubmenu }">
                  <span>Sort</span>
                  <svg class="w-4 h-4 transition-transform duration-200" :class="{ 'rotate-180': showSortSubmenu }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                <div v-if="showSortSubmenu" class="border-t border-gray-200">
                  <button 
                    @click="setSortOption('sample_id', 'asc'); showActionsMenu = false" 
                    class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    :class="{ 'bg-blue-50 text-blue-700': sortField === 'sample_id' && sortDirection === 'asc' }"
                  >
                    Sample ID (A-Z)
                  </button>
                  <button 
                    @click="setSortOption('sample_id', 'desc'); showActionsMenu = false" 
                    class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    :class="{ 'bg-blue-50 text-blue-700': sortField === 'sample_id' && sortDirection === 'desc' }"
                  >
                    Sample ID (Z-A)
                  </button>
                  <button 
                    @click="setSortOption('collected_date', 'asc'); showActionsMenu = false" 
                    class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    :class="{ 'bg-blue-50 text-blue-700': sortField === 'collected_date' && sortDirection === 'asc' }"
                  >
                    Collection Date (Earliest)
                  </button>
                  <button 
                    @click="setSortOption('collected_date', 'desc'); showActionsMenu = false" 
                    class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    :class="{ 'bg-blue-50 text-blue-700': sortField === 'collected_date' && sortDirection === 'desc' }"
                  >
                    Collection Date (Latest)
                  </button>
                </div>
              </div>
              
              <!-- Add Samples Section -->
              <div class="border-t border-gray-200" v-if="(samples.length > 0 && showSortSubmenu) || samples.length === 0">
                <button @click="showAddSubmenu = !showAddSubmenu" class="flex items-center justify-between w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" :class="{ 'bg-gray-50': showAddSubmenu }">
                  <span>Add Samples</span>
                  <svg class="w-4 h-4 transition-transform duration-200" :class="{ 'rotate-180': showAddSubmenu }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                <div v-if="showAddSubmenu" class="border-t border-gray-200">
                  <button @click="startAddingSamples(1); showActionsMenu = false" class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50">Add 1 Sample</button>
                  <button @click="startAddingSamples(2); showActionsMenu = false" class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50">Add 2 Samples</button>
                  <button @click="startAddingSamples(3); showActionsMenu = false" class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50">Add 3 Samples</button>
                  <button @click="startAddingSamples(4); showActionsMenu = false" class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50">Add 4 Samples</button>
                  <button @click="startAddingSamples(5); showActionsMenu = false" class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50">Add 5 Samples</button>
                  <button @click="startAddingSamples(10); showActionsMenu = false" class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50">Add 10 Samples</button>
                </div>
              </div>
              <div v-else>
                <button @click="showAddSubmenu = !showAddSubmenu" class="flex items-center justify-between w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" :class="{ 'bg-gray-50': showAddSubmenu }">
                  <span>Add Samples</span>
                  <svg class="w-4 h-4 transition-transform duration-200" :class="{ 'rotate-180': showAddSubmenu }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                <div v-if="showAddSubmenu" class="border-t border-gray-200">
                  <button @click="startAddingSamples(1); showActionsMenu = false" class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50">Add 1 Sample</button>
                  <button @click="startAddingSamples(2); showActionsMenu = false" class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50">Add 2 Samples</button>
                  <button @click="startAddingSamples(3); showActionsMenu = false" class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50">Add 3 Samples</button>
                  <button @click="startAddingSamples(4); showActionsMenu = false" class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50">Add 4 Samples</button>
                  <button @click="startAddingSamples(5); showActionsMenu = false" class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50">Add 5 Samples</button>
                  <button @click="startAddingSamples(10); showActionsMenu = false" class="block w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-50">Add 10 Samples</button>
                </div>
              </div>
              
              <!-- Assign Samples Section (Expandable) -->
              <div v-if="samples.length > 0" class="border-t border-gray-200">
                <button @click="showAssignSubmenu = !showAssignSubmenu" class="flex items-center justify-between w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" :class="{ 'bg-gray-50': showAssignSubmenu }">
                  <span>Assign Samples</span>
                  <svg class="w-4 h-4 transition-transform duration-200" :class="{ 'rotate-180': showAssignSubmenu }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                <div v-if="showAssignSubmenu" class="border-t border-gray-200">
                  <button
                    @click="rematchAll(); showActionsMenu = false"
                    :disabled="isManualTask"
                    class="block w-full text-left px-6 py-2 text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    :class="isManualTask ? 'text-gray-400' : 'text-gray-700'"
                    :title="isManualTask ? 'Not available for manual tasks' : 'Re-match all samples to parcels'"
                  >
                    Assign All
                  </button>
                  <button
                    @click="matchUnassigned(); showActionsMenu = false"
                    :disabled="isManualTask"
                    class="block w-full text-left px-6 py-2 text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    :class="isManualTask ? 'text-gray-400' : 'text-gray-700'"
                    :title="isManualTask ? 'Not available for manual tasks' : 'Match unassigned samples to parcels'"
                  >
                    Assign Unassigned
                  </button>
                </div>
              </div>

              <!-- Export CSV -->
              <div class="border-t border-gray-200">
                <button
                  @click="exportRecordsCsv(); showActionsMenu = false"
                  :disabled="sortedSamples.length === 0"
                  class="flex items-center w-full text-left px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  :class="sortedSamples.length === 0 ? 'text-gray-400' : 'text-gray-700'"
                  :title="sortedSamples.length === 0 ? 'No records to export' : 'Download these records as a CSV file, with every column included'"
                >
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="newSamples.length > 0" class="flex items-center space-x-3">
          <!-- Multi-sample progress indicator -->
          <div v-if="newSamples.length > 1" class="text-sm text-gray-600">
            {{ newSamples.filter(s => s.georeferences && s.georeferences.length > 0).length }}/{{ newSamples.length }} samples have georeferences
          </div>
          
          <button
            @click="saveAllSamples"
            :disabled="!canSaveAnySample || isCreating"
            class="px-4 py-2 bg-green-600 border border-transparent rounded-md font-semibold text-sm text-white hover:bg-green-700 disabled:opacity-25 transition ease-in-out duration-150"
          >
            {{ newSamples.length === 1 ? 'Save Sample' : `Save All ${validSampleCount} Sample${validSampleCount !== 1 ? 's' : ''}` }}
          </button>
        </div>
      </div>
    </div>
    <div class="mt-2 pb-2">
      <div class="overflow-x-auto min-w-0">
        <table
          class="min-w-full w-full table-auto divide-y divide-gray-400 border-b border-gray-400 border-collapse rounded-lg"
        >
        <thead>
          <tr class="bg-gray-200 font-bold text-sm text-gray-900">
            <th v-if="columnVisibility && columnVisibility.sampleId" scope="col" class="px-3 py-2 text-left cursor-pointer hover:bg-gray-300 select-none whitespace-nowrap" style="min-width: 120px;" @click="sortBy('sample_id')" title="Click to sort by Sample ID">
              <div class="flex items-center justify-between">
                <span>Sample ID</span>
                <div class="flex flex-col text-xs leading-none ml-1">
                  <span :class="sortField === 'sample_id' && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-400'">▲</span>
                  <span :class="sortField === 'sample_id' && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-400'">▼</span>
                </div>
              </div>
            </th>
            <th v-if="columnVisibility && columnVisibility.test" scope="col" class="px-1 py-2 text-left">
              <div class="flex items-center space-x-1">
                <span>Test</span>
                <button
                  @click="showLabTestInfo = true"
                  class="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                  type="button"
                  title="View test descriptions"
                >
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </th>
            <th v-if="columnVisibility && columnVisibility.reference" scope="col" class="px-3 py-2 text-left">Ref.</th>
            <th v-if="columnVisibility && columnVisibility.soilType" scope="col" class="px-3 py-2 text-left">Soil Type</th>
            <th v-if="columnVisibility && columnVisibility.assigned" scope="col" class="px-3 py-2 text-center">Assigned</th>
            <th v-if="columnVisibility && columnVisibility.farmName" scope="col" class="px-1 py-2 text-left">Farm</th>
            <th v-if="columnVisibility && columnVisibility.parcelNo" scope="col" class="px-1 py-2 text-left">Parcel No</th>
            <th v-if="columnVisibility && columnVisibility.parcelName" scope="col" class="px-1 py-2 text-left">Parcel Name</th>
            <th v-if="columnVisibility && columnVisibility.comment" scope="col" class="px-1 py-2 text-center">Comment</th>
            <th v-if="columnVisibility && columnVisibility.lpis" scope="col" class="px-1 py-2 text-center">
              <div class="flex items-center justify-center space-x-1">
                <span>LPIS</span>
                <button
                  @click="showGeoreferenceInfo = true"
                  class="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                  type="button"
                  title="View georeference legal requirements"
                >
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </th>
            <th v-if="columnVisibility && columnVisibility.collected" scope="col" class="px-1 py-2 text-left cursor-pointer hover:bg-gray-300 select-none" @click="sortBy('collected_date')" title="Click to sort by Collected Date (SMAP: sample end date, Manual: created date)">
              <div class="flex items-center justify-between">
                <span>Collected</span>
                <div class="flex flex-col text-xs leading-none ml-1">
                  <span :class="sortField === 'collected_date' && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-400'">▲</span>
                  <span :class="sortField === 'collected_date' && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-400'">▼</span>
                </div>
              </div>
            </th>
            <th v-if="columnVisibility && columnVisibility.georef" scope="col" class="px-1 py-2 text-center" title="Georeferences">
              <div class="flex items-center justify-center space-x-1">
                <span>Georeference</span>
                <button
                  @click="showGeoreferenceInfo = true"
                  class="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                  type="button"
                  title="View georeference legal requirements"
                >
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </th>
            <th v-if="columnVisibility && columnVisibility.result" scope="col" class="px-1 py-2 text-center">Result</th>
            <!-- Lab Results Columns -->
            <th v-if="columnVisibility && columnVisibility.labPh && dataType !== 'micro'" scope="col" class="px-1 py-2 text-center">pH</th>
            <th v-if="columnVisibility && columnVisibility.labLimeReq" scope="col" class="px-1 py-2 text-center">Lime Req</th>
            <th v-if="columnVisibility && columnVisibility.labPhosphorus" scope="col" class="px-1 py-2 text-center">P (mg/l)</th>
            <th v-if="columnVisibility && columnVisibility.labPIndexGrassland" scope="col" class="px-1 py-2 text-center">P Index (Grass)</th>
            <th v-if="columnVisibility && columnVisibility.labPIndexOther" scope="col" class="px-1 py-2 text-center">P Index (Other)</th>
            <th v-if="columnVisibility && columnVisibility.labPotassium" scope="col" class="px-1 py-2 text-center">K (mg/l)</th>
            <th v-if="columnVisibility && columnVisibility.labKIndexMineral" scope="col" class="px-1 py-2 text-center">K Index (Mineral)</th>
            <th v-if="columnVisibility && columnVisibility.labKIndexPeat" scope="col" class="px-1 py-2 text-center">K Index (Peat)</th>
            <th v-if="columnVisibility && columnVisibility.labMagnesium" scope="col" class="px-1 py-2 text-center">Mg</th>
            <th v-if="columnVisibility && columnVisibility.labCalcium" scope="col" class="px-1 py-2 text-center">Ca</th>
            <th v-if="columnVisibility && columnVisibility.labCopper" scope="col" class="px-1 py-2 text-center">Cu</th>
            <th v-if="columnVisibility && columnVisibility.labManganese" scope="col" class="px-1 py-2 text-center">Mn</th>
            <th v-if="columnVisibility && columnVisibility.labZinc" scope="col" class="px-1 py-2 text-center">Zn</th>
            <th v-if="columnVisibility && columnVisibility.labBoron" scope="col" class="px-1 py-2 text-center">B</th>
            <!-- Micronutrient Status Columns -->
            <th v-if="columnVisibility && columnVisibility.labMagnesiumStatus" scope="col" class="px-1 py-2 text-center">Mg Status</th>
            <th v-if="columnVisibility && columnVisibility.labCalciumStatus" scope="col" class="px-1 py-2 text-center">Ca Status</th>
            <th v-if="columnVisibility && columnVisibility.labCopperStatus" scope="col" class="px-1 py-2 text-center">Cu Status</th>
            <th v-if="columnVisibility && columnVisibility.labManganeseStatus" scope="col" class="px-1 py-2 text-center">Mn Status</th>
            <th v-if="columnVisibility && columnVisibility.labZincStatus" scope="col" class="px-1 py-2 text-center">Zn Status</th>
            <th v-if="columnVisibility && columnVisibility.labBoronStatus" scope="col" class="px-1 py-2 text-center">B Status</th>
            <th v-if="columnVisibility && columnVisibility.labOm" scope="col" class="px-1 py-2 text-center">
              <div class="flex items-center justify-center space-x-1">
                <span>OM</span>
                <button
                  @click="showOmInfo = true"
                  class="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                  type="button"
                  title="View organic matter threshold information"
                >
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </th>
            <th v-if="columnVisibility && columnVisibility.labTestDate" scope="col" class="px-1 py-2 text-center">Test Date</th>
            <!-- Fixed width locate button column -->
            <th scope="col" class="px-1 py-2 text-center w-8" title="Locate on Map"></th>
            <!-- Fixed width actions column -->
            <th scope="col" class="px-1 py-2 text-center w-10" title="Sample Actions"></th>
          </tr>
        </thead>
        <tbody>
          <!-- New Sample Rows -->
          <tr v-for="(newSample, index) in newSamples" :key="`new-${index}`" 
              class="h-12 bg-green-50 border-2 border-green-200 text-gray-900 text-sm"
              :class="{ 'border-t-0': index > 0 }">
            <!-- Sample ID Input - only takes its own column space -->
            <td v-if="columnVisibility && columnVisibility.sampleId" class="px-1 py-1.5">
              <input 
                v-model="newSample.sample_id" 
                type="text" 
                :placeholder="`Suggested: ${getSuggestedIdForIndex(index)}`"
                @input="onSampleIdChange(index)"
                @blur="refreshIdSuggestions"
                :class="{
                  'w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500': true,
                  'border-red-300 focus:border-red-500': isDuplicateSampleId(newSample.sample_id, index),
                  'border-green-300 focus:border-green-500': !isDuplicateSampleId(newSample.sample_id, index) && newSample.sample_id === getSuggestedIdForIndex(index),
                  'border-gray-300 focus:border-blue-500': !isDuplicateSampleId(newSample.sample_id, index) && newSample.sample_id !== getSuggestedIdForIndex(index)
                }"
                @keydown="handleNewSampleKeyDown($event, index, 'sample_id')"
                :title="newSample.sample_id === getSuggestedIdForIndex(index) ? 'Using suggested ID' : 'Custom ID entered'"
              />
              <div v-if="isDuplicateSampleId(newSample.sample_id, index)" class="text-xs text-red-600 mt-1">
                ID already exists
              </div>
            </td>
            <!-- Test column - always show when test column is visible -->
            <td v-if="columnVisibility && columnVisibility.test" class="px-1 py-1.5 relative">
              <div class="flex items-center space-x-0.5 w-full max-w-full">
                <select 
                  v-model="newSample.lab_test_string"
                  class="w-full min-w-0 px-2 py-0.5 pr-8 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 truncate"
                  @keydown="handleNewSampleKeyDown($event, index, 'lab_test')"
                  @change="copyLabTestToSubsequentRows(index)"
                >
                  <option value="">Select</option>
                  <option v-for="(name, code) in transformedLabTests" :key="code" :value="code">
                    {{ name }}
                  </option>
                </select>
              </div>
            </td>
            <!-- Reference column - only show if Sample ID is not visible or if both are visible but spanned by Sample ID -->
            <td v-if="columnVisibility && columnVisibility.reference && !columnVisibility.sampleId" class="px-1 py-1.5">
              <!-- Empty for new samples -->
            </td>
            <!-- Soil type is set by the SMAP form or on the edit sample page -->
            <td v-if="columnVisibility && columnVisibility.soilType" class="px-1 py-1.5">
            </td>
            <!-- Assigned -->
            <td v-if="columnVisibility && columnVisibility.assigned" class="px-1 py-1.5 text-center">
              <div v-if="newSample.parcelInfo" class="flex justify-center">
                <div
                  class="w-4 h-4 rounded-full bg-green-600"
                  :title="`${newSample.parcelInfo.farm_name || ''} - Parcel: ${newSample.parcelInfo.parcel_no || 'N/A'} ${newSample.parcelInfo.parcel_name ? '(' + newSample.parcelInfo.parcel_name + ')' : ''}`"
                >
                  <span>&nbsp;</span>
                </div>
              </div>
              <div v-else class="text-xs text-gray-400">
                -
              </div>
            </td>
            <!-- Farm Name -->
            <td v-if="columnVisibility && columnVisibility.farmName" class="px-1 py-1.5">
              <span v-if="newSample.farmName" class="text-sm text-gray-700" :title="newSample.farmName">
                {{ newSample.farmName }}
              </span>
            </td>
            <!-- Parcel No -->
            <td v-if="columnVisibility && columnVisibility.parcelNo" class="px-1 py-1.5">
              <span v-if="newSample.parcelInfo && newSample.parcelInfo.parcel_no" class="text-sm text-gray-700" :title="newSample.parcelInfo.parcel_no">
                {{ newSample.parcelInfo.parcel_no }}
              </span>
            </td>
            <!-- Parcel Name -->
            <td v-if="columnVisibility && columnVisibility.parcelName" class="px-1 py-1.5">
              <span v-if="newSample.parcelName" class="text-sm text-gray-700" :title="newSample.parcelName">
                {{ newSample.parcelName }}
              </span>
            </td>
            <!-- Comment Input -->
            <td v-if="columnVisibility && columnVisibility.comment" class="px-1 py-1.5 text-center">
              <button
                @click="openCommentModal(index)"
                class="inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                :class="newSample.comment ? 'text-blue-600' : 'text-gray-400'"
                :title="newSample.comment ? `Comment: ${newSample.comment}` : 'Add comment'"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </td>
            <!-- LPIS Input -->
            <td v-if="columnVisibility && columnVisibility.lpis" class="px-1 py-1.5 text-center">
              <button
                @click="openLpisModal(index)"
                class="inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                :class="newSample.lpis ? 'text-green-600' : 'text-gray-400'"
                :title="newSample.lpis ? `LPIS: ${newSample.lpis}` : 'Add LPIS'"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </td>
            <!-- Collected Date (empty for new samples) -->
            <td v-if="columnVisibility && columnVisibility.collected" class="px-1 py-1.5">
              <span class="text-xs text-gray-500"></span>
            </td>
            <!-- Georeferences -->
            <td v-if="columnVisibility && columnVisibility.georef" class="px-0.5 py-1.5 w-10">
              <div v-if="!isSelectingGeoreferences || currentSampleIndex !== index">
                <!-- Normal state: Set/Edit button with parcel option if available -->
                <div class="flex flex-col space-y-1">
                  <!-- Main georeference button -->
                  <div class="flex flex-col space-y-0.5">
                    <!-- Show parcel button always, but disabled when no parcels available -->
                    <button
                      @click="selectParcelGeoreference(index)"
                      :disabled="!hasAvailableParcels"
                      class="px-1 py-0.5 text-xs rounded focus:outline-none focus:ring-2 whitespace-nowrap"
                      :class="[
                        hasAvailableParcels ? [
                          'text-white focus:ring-purple-500',
                          newSample.georeferences.length > 0 && newSample.selectionMethod === 'parcel'
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-purple-600 hover:bg-purple-700'
                        ] : [
                          'text-gray-500 bg-gray-200 cursor-not-allowed'
                        ]
                      ]"
                      :title="!hasAvailableParcels 
                        ? 'No parcels available. Add or draw parcels first to use this feature.'
                        : newSample.georeferences.length > 0 && newSample.selectionMethod === 'parcel' 
                          ? 'Edit parcel selection' 
                          : 'Click on a parcel to use its center point'"
                    >
                      Select Parcel
                    </button>
                    
                    <!-- Show manual pin button (always available) -->
                    <div class="flex space-x-0.5">
                      <button
                        @click="selectGeoreferencesForNewSample(index)"
                        class="px-1 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap flex-1"
                        :class="{
                          'bg-green-600 hover:bg-green-700': newSample.georeferences.length > 0 && newSample.selectionMethod === 'manual',
                          'bg-blue-600 hover:bg-blue-700': !(newSample.georeferences.length > 0 && newSample.selectionMethod === 'manual')
                        }"
                        :title="newSample.selectionMethod === 'manual' ? 'Edit manual points' : 'Set points manually on the map'"
                      >
                        {{ newSample.selectionMethod === 'manual' && newSample.georeferences.length > 0 ? `${newSample.georeferences.length} point${newSample.georeferences.length !== 1 ? 's' : ''}` : 'Set Points' }}
                      </button>
                      
                      <!-- Clear All button when points exist -->
                      <button
                        v-if="newSample.georeferences.length > 0"
                        @click="clearSampleGeoreferences(index)"
                        class="p-0.5 text-gray-400 hover:text-gray-600 focus:outline-none flex-shrink-0"
                        type="button"
                        title="Clear all georeferences for this sample"
                      >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-else class="space-y-1">
                <!-- Selection state: Finish and Cancel buttons with sample info -->
                
                <div class="flex space-x-0.5">
                  <button
                    @click="finishGeoreferenceSelection()"
                    class="px-1.5 py-0.5 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    Finish ({{ newSample.georeferences.length }})
                  </button>
                  <button
                    @click="cancelGeoreferenceSelection()"
                    class="px-1.5 py-0.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    title="Cancel georeference selection"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </td>
            <!-- Lab Results (empty for new samples) -->
            <td v-if="columnVisibility && columnVisibility.result" class="px-1 py-1.5">
              <div class="flex items-center justify-center">
                <button
                  v-if="newSample.georeferences.length > 0"
                  @click="locateNewSample(index)"
                  class="inline-flex items-center justify-center w-7 h-7 rounded-full border border-gray-300 bg-white hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  :title="`Locate ${newSample.georeferences.length} georeference point${newSample.georeferences.length !== 1 ? 's' : ''} on map`"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5 text-gray-600">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span class="sr-only">Locate sample</span>
                </button>
              </div>
            </td>
            <!-- Lab Result Columns (empty for new samples) -->
            <td v-if="columnVisibility && columnVisibility.labPh && dataType !== 'micro'" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labLimeReq" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labPhosphorus" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labPIndexGrassland" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labPIndexOther" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labPotassium" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labKIndexMineral" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labKIndexPeat" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labMagnesium" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labCalcium" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labCopper" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labManganese" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labZinc" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labBoron" class="px-1 py-1.5"></td>
            <!-- Micronutrient Status Columns (empty for new samples) -->
            <td v-if="columnVisibility && columnVisibility.labMagnesiumStatus" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labCalciumStatus" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labCopperStatus" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labManganeseStatus" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labZincStatus" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labBoronStatus" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labOm" class="px-1 py-1.5"></td>
            <td v-if="columnVisibility && columnVisibility.labTestDate" class="px-1 py-1.5"></td>
            <!-- Locate Button (empty for new samples) -->
            <td class="px-0.5 py-1.5 w-12">
              <!-- Empty for new samples -->
            </td>
            <!-- Actions -->
            <td class="px-0.5 py-1.5 w-20">
              <div class="flex flex-col space-y-0.5">
                <button
                  v-if="index === newSamples.length - 1"
                  @click="addAnotherSample(index)"
                  :disabled="isSelectingGeoreferences"
                  class="px-1 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  :title="isSelectingGeoreferences ? 'Finish setting georeferences first' : 'Add another sample row'"
                >
                  + Add
                </button>
                <button
                  @click="removeSampleRow(index)"
                  class="px-1 py-0.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 whitespace-nowrap"
                  :title="newSamples.length > 1 ? 'Remove this row' : 'Cancel all'"
                >
                  {{ newSamples.length > 1 ? '✕' : 'Cancel' }}
                </button>
              </div>
            </td>
          </tr>
          
          <!-- Existing Samples -->
          <tr
            v-for="(sample, index) in sortedSamples"
            :key="index"
            class="h-12 odd:bg-white even:bg-slate-100 text-gray-900 text-sm"
            :class="index % 2 == 0 ? 'bg-white' : 'bg-gray-50'"
          >
            <!-- Sample ID -->
            <td v-if="columnVisibility && columnVisibility.sampleId" class="px-3 py-2 whitespace-nowrap" style="min-width: 120px;">
              {{ sample.id }}
            </td>
            <!-- Lab Tests -->
            <td v-if="columnVisibility && columnVisibility.test" class="px-1 py-2">
              {{ getLabTestsDisplay(sample) }}
            </td>
            <!-- Sample Reference -->
            <td v-if="columnVisibility && columnVisibility.reference" class="px-3 py-2">
              <span v-if="sample.sample_reference" class="text-sm">
                {{ sample.sample_reference }}
              </span>
              <span v-else class="text-sm text-gray-500">-</span>
            </td>
            <!-- Soil Type -->
            <td v-if="columnVisibility && columnVisibility.soilType" class="px-3 py-2">
              <span v-if="sample.soil_type" class="text-sm">
                {{ sample.soil_type }}
              </span>
              <span v-else class="text-sm text-gray-500">-</span>
            </td>
            <!-- Assigned -->
            <td v-if="columnVisibility && columnVisibility.assigned" class="px-3 py-2 text-center">
              <div v-if="getParcelNumber(sample)" class="flex justify-center">
                <div v-if="task.organisation_name == 'ISEC'">
                  <div
                    class="w-4 h-4 rounded-full"
                    :class="{
                      'bg-red-600':
                        (sample.matched &&
                          sample.zone_issues &&
                          sample.lpis_issue) ||
                        (!sample.matched && sample.lpis_issue),
                      'bg-yellow-600':
                        (sample.matched &&
                          (sample.zone_issues || sample.lpis_issue) &&
                          sample.zone_issues != sample.lpis_issue) ||
                        (!sample.matched && !sample.lpis_issue),
                      'bg-green-600':
                        sample.matched &&
                        !sample.zone_issues &&
                        !sample.lpis_issue,
                    }"
                    :title="`${sample.farm_name || ''} - Parcel: ${getParcelNumber(sample)} ${getParcelName(sample) ? '(' + getParcelName(sample) + ')' : ''}`"
                  >
                    <span>&nbsp;</span>
                  </div>
                </div>
                <div v-else>
                  <div
                    v-if="sample.matched"
                    class="w-4 h-4 rounded-full"
                    :class="sample.zone_issues ? 'bg-yellow-600' : 'bg-green-600'"
                    :title="`${sample.farm_name || ''} - Parcel: ${getParcelNumber(sample)} ${getParcelName(sample) ? '(' + getParcelName(sample) + ')' : ''}`"
                  >
                    <span>&nbsp;</span>
                  </div>
                </div>
              </div>
              <div v-else-if="!sample.matched" class="text-xs text-gray-400">
                -
              </div>
            </td>
            <!-- Farm Name -->
            <td v-if="columnVisibility && columnVisibility.farmName" class="px-1 py-2">
              {{ sample.farm_name }}
            </td>
            <!-- Parcel No -->
            <td v-if="columnVisibility && columnVisibility.parcelNo" class="px-1 py-2">
              <div v-if="getParcelNumber(sample)" class="text-sm">
                <span class="truncate" :title="getParcelNumber(sample)">
                  {{ getParcelNumber(sample) }}
                </span>
              </div>
              <div v-else class="text-sm text-gray-500">
                -
              </div>
            </td>
            <!-- Parcel Name -->
            <td v-if="columnVisibility && columnVisibility.parcelName" class="px-1 py-2">
              <span v-if="getParcelName(sample)" class="text-sm truncate" :title="getParcelName(sample)">
                {{ getParcelName(sample) }}
              </span>
              <span v-else class="text-sm text-gray-500">-</span>
            </td>
            <!-- Sample Comment -->
            <td v-if="columnVisibility && columnVisibility.comment" class="px-1 py-2 text-center">
              <span v-if="sample.comment" class="text-sm">
                {{ sample.comment }}
              </span>
              <span v-else class="text-sm text-gray-500">-</span>
            </td>
            <!-- LPIS -->
            <td v-if="columnVisibility && columnVisibility.lpis" class="px-1 py-2 text-center">
              <span v-if="sample.lpis" class="text-sm">
                {{ sample.lpis }}
              </span>
              <span v-else class="text-sm text-gray-500">-</span>
            </td>
            <!-- Collected Date -->
            <td v-if="columnVisibility && columnVisibility.collected" class="px-1 py-2">
              <span class="text-sm" :title="getCollectedDateTooltip(sample)">
                {{ formatCollectedDate(sample) }}
              </span>
            </td>
            <!-- Georeferences -->
            <td v-if="columnVisibility && columnVisibility.georef" class="px-1 py-2">
              <div class="flex items-center justify-center">
                <!-- Georeference count display -->
                <button
                  v-if="getGeoreferenceCount(sample) > 0"
                  @click="showCoordinatesForSample(sample)"
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer transition-colors duration-150"
                  :title="`Click to view ${getGeoreferenceCount(sample)} coordinate${getGeoreferenceCount(sample) !== 1 ? 's' : ''}`"
                >
                  📍 {{ getGeoreferenceCount(sample) }}
                </button>
                <span 
                  v-else
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                  title="No georeference points set"
                >
                  📍 0
                </span>
                
                <!-- Edit Markers button or Finish/Cancel buttons for SMAP samples -->
                <div v-if="isSMAPSample(sample) && canCreateSamples" class="ml-1">
                  <!-- Normal state: Edit Markers button -->
                  <button
                    v-if="!isEditingMarkers || editingMarkerSample?.hashid !== sample.hashid"
                    @click="openMarkerEditModal(sample)"
                    class="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors duration-150"
                    title="Edit georeference markers"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2-2v-7"/>
                      <path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  
                  <!-- Editing state: Finish and Cancel buttons -->
                  <div v-else class="flex space-x-0.5">
                    <button
                      @click="finishMarkerSelection()"
                      class="px-1.5 py-0.5 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      :title="`Finish marker selection (${tempMarkerIndices.length} selected)`"
                    >
                      Finish ({{ tempMarkerIndices.length }})
                    </button>
                    <button
                      @click="cancelMarkerSelection()"
                      class="px-1.5 py-0.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400"
                      title="Cancel marker selection"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </td>
            <!-- Lab Results -->
            <td v-if="columnVisibility && columnVisibility.result" class="px-1 py-2">
              <div
                v-if="sample.lab_results"
                class="w-4 h-4 mx-auto rounded-full bg-green-600"
              >
                <span>&nbsp;</span>
              </div>
            </td>
            <!-- Lab Result Data Columns -->
            <td v-if="columnVisibility && columnVisibility.labPh && dataType !== 'micro'" class="px-1 py-2 text-center text-xs">
              {{ formatSoilTestValue(sample.lab_results?.pH) || '-' }}
            </td>
            <td v-if="columnVisibility && columnVisibility.labLimeReq" class="px-1 py-2 text-center text-xs">
              <span v-if="sample.lab_results?.lime_req_6_5 && sample.lab_results?.lime_req_6_5 !== 'None' && sample.lab_results?.lime_index_colour" :style="getSoilTestPillStyle(sample.lab_results?.lime_index_colour)">
                {{ sample.lab_results?.lime_req_6_5 }}
              </span>
              <span v-else>{{ sample.lab_results?.lime_req_6_5 || '-' }}</span>
            </td>
            <td v-if="columnVisibility && columnVisibility.labPhosphorus" class="px-1 py-2 text-center text-xs">
              {{ formatSoilTestValue(sample.lab_results?.phosphorus) || '-' }}
            </td>
            <td v-if="columnVisibility && columnVisibility.labPIndexGrassland" class="px-1 py-2 text-center text-xs">
              <span v-if="sample.lab_results?.phosphorus_index_grassland !== null && sample.lab_results?.phosphorus_index_grassland_colour" :style="getSoilTestPillStyle(sample.lab_results?.phosphorus_index_grassland_colour)">
                {{ sample.lab_results?.phosphorus_index_grassland }}
              </span>
              <span v-else>{{ sample.lab_results?.phosphorus_index_grassland || '-' }}</span>
            </td>
            <td v-if="columnVisibility && columnVisibility.labPIndexOther" class="px-1 py-2 text-center text-xs">
              <span v-if="sample.lab_results?.phosphorus_index_other !== null && sample.lab_results?.phosphorus_index_other_colour" :style="getSoilTestPillStyle(sample.lab_results?.phosphorus_index_other_colour)">
                {{ sample.lab_results?.phosphorus_index_other }}
              </span>
              <span v-else>{{ sample.lab_results?.phosphorus_index_other || '-' }}</span>
            </td>
            <td v-if="columnVisibility && columnVisibility.labPotassium" class="px-1 py-2 text-center text-xs">
              {{ formatSoilTestValue(sample.lab_results?.potassium, 0) || '-' }}
            </td>
            <td v-if="columnVisibility && columnVisibility.labKIndexMineral" class="px-1 py-2 text-center text-xs">
              <span v-if="sample.lab_results?.potassium_index_mineral !== null && sample.lab_results?.potassium_index_mineral_colour" :style="getSoilTestPillStyle(sample.lab_results?.potassium_index_mineral_colour)">
                {{ sample.lab_results?.potassium_index_mineral }}
              </span>
              <span v-else>{{ sample.lab_results?.potassium_index_mineral || '-' }}</span>
            </td>
            <td v-if="columnVisibility && columnVisibility.labKIndexPeat" class="px-1 py-2 text-center text-xs">
              <span v-if="sample.lab_results?.potassium_index_peat !== null && sample.lab_results?.potassium_index_peat_colour" :style="getSoilTestPillStyle(sample.lab_results?.potassium_index_peat_colour)">
                {{ sample.lab_results?.potassium_index_peat }}
              </span>
              <span v-else>{{ sample.lab_results?.potassium_index_peat || '-' }}</span>
            </td>
            <td v-if="columnVisibility && columnVisibility.labMagnesium" class="px-1 py-2 text-center text-xs">
              {{ formatSoilTestValue(sample.lab_results?.magnesium, 0) || '-' }}
            </td>
            <td v-if="columnVisibility && columnVisibility.labCalcium" class="px-1 py-2 text-center text-xs">
              {{ formatSoilTestValue(sample.lab_results?.calcium, 0) || '-' }}
            </td>
            <td v-if="columnVisibility && columnVisibility.labCopper" class="px-1 py-2 text-center text-xs">
              {{ formatSoilTestValue(sample.lab_results?.copper) || '-' }}
            </td>
            <td v-if="columnVisibility && columnVisibility.labManganese" class="px-1 py-2 text-center text-xs">
              {{ formatSoilTestValue(sample.lab_results?.manganese, 1) || '-' }}
            </td>
            <td v-if="columnVisibility && columnVisibility.labZinc" class="px-1 py-2 text-center text-xs">
              {{ formatSoilTestValue(sample.lab_results?.zinc) || '-' }}
            </td>
            <td v-if="columnVisibility && columnVisibility.labBoron" class="px-1 py-2 text-center text-xs">
              {{ formatSoilTestValue(sample.lab_results?.boron, 2) || '-' }}
            </td>
            <!-- Micronutrient Status Columns -->
            <td v-if="columnVisibility && columnVisibility.labMagnesiumStatus" class="px-1 py-2 text-center text-xs">
              <span v-if="sample.lab_results?.magnesium_status && sample.lab_results?.magnesium_status_colour" :style="getSoilTestPillStyle(sample.lab_results?.magnesium_status_colour)">
                {{ abbreviateStatus(sample.lab_results?.magnesium_status) }}
              </span>
              <span v-else>{{ abbreviateStatus(sample.lab_results?.magnesium_status) }}</span>
            </td>
            <td v-if="columnVisibility && columnVisibility.labCalciumStatus" class="px-1 py-2 text-center text-xs">
              <span v-if="sample.lab_results?.calcium_status && sample.lab_results?.calcium_status_colour" :style="getSoilTestPillStyle(sample.lab_results?.calcium_status_colour)">
                {{ abbreviateStatus(sample.lab_results?.calcium_status) }}
              </span>
              <span v-else>{{ abbreviateStatus(sample.lab_results?.calcium_status) }}</span>
            </td>
            <td v-if="columnVisibility && columnVisibility.labCopperStatus" class="px-1 py-2 text-center text-xs">
              <span v-if="sample.lab_results?.copper_status && sample.lab_results?.copper_status_colour" :style="getSoilTestPillStyle(sample.lab_results?.copper_status_colour)">
                {{ abbreviateStatus(sample.lab_results?.copper_status) }}
              </span>
              <span v-else>{{ abbreviateStatus(sample.lab_results?.copper_status) }}</span>
            </td>
            <td v-if="columnVisibility && columnVisibility.labManganeseStatus" class="px-1 py-2 text-center text-xs">
              <span v-if="sample.lab_results?.manganese_status && sample.lab_results?.manganese_status_colour" :style="getSoilTestPillStyle(sample.lab_results?.manganese_status_colour)">
                {{ abbreviateStatus(sample.lab_results?.manganese_status) }}
              </span>
              <span v-else>{{ abbreviateStatus(sample.lab_results?.manganese_status) }}</span>
            </td>
            <td v-if="columnVisibility && columnVisibility.labZincStatus" class="px-1 py-2 text-center text-xs">
              <span v-if="sample.lab_results?.zinc_status && sample.lab_results?.zinc_status_colour" :style="getSoilTestPillStyle(sample.lab_results?.zinc_status_colour)">
                {{ abbreviateStatus(sample.lab_results?.zinc_status) }}
              </span>
              <span v-else>{{ abbreviateStatus(sample.lab_results?.zinc_status) }}</span>
            </td>
            <td v-if="columnVisibility && columnVisibility.labBoronStatus" class="px-1 py-2 text-center text-xs">
              <span v-if="sample.lab_results?.boron_status && sample.lab_results?.boron_status_colour" :style="getSoilTestPillStyle(sample.lab_results?.boron_status_colour)">
                {{ abbreviateStatus(sample.lab_results?.boron_status) }}
              </span>
              <span v-else>{{ abbreviateStatus(sample.lab_results?.boron_status) }}</span>
            </td>
            <td v-if="columnVisibility && columnVisibility.labOm" class="px-1 py-2 text-center text-xs">
              <span v-if="sample.lab_results?.OM !== null && sample.lab_results?.organic_matter_colour" :style="getSoilTestPillStyle(sample.lab_results?.organic_matter_colour)">
                {{ formatSoilTestValue(sample.lab_results?.OM) }}
              </span>
              <span v-else>{{ formatSoilTestValue(sample.lab_results?.OM) || '-' }}</span>
            </td>
            <td v-if="columnVisibility && columnVisibility.labTestDate" class="px-1 py-2 text-center text-xs">
              {{ sample.lab_results?.test_date || '-' }}
            </td>
            <!-- Locate Button -->
            <td class="px-0.5 py-2 w-12">
              <div class="flex items-center justify-center">
                <button
                  v-if="getGeoreferenceCount(sample) > 0 || isSMAPSample(sample)"
                  @click="locateExistingSample(sample)"
                  class="inline-flex items-center justify-center w-7 h-7 rounded-full border border-gray-300 bg-white hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  :title="getGeoreferenceCount(sample) > 0 ? `Locate ${getGeoreferenceCount(sample)} georeference point${getGeoreferenceCount(sample) !== 1 ? 's' : ''} on map` : 'Locate GPS path on map'"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5 text-gray-600">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span class="sr-only">Locate sample</span>
                </button>
              </div>
            </td>
            <!-- Actions Dropdown -->
            <td class="px-0.5 py-2 relative w-20">
              <div class="relative flex justify-center">
                <button 
                  @click="toggleSampleDropdown(sample.hashid)"
                  @blur="closeSampleDropdown(sample.hashid)"
                  class="p-1 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  :title="'Actions for sample ' + sample.id"
                >
                  <svg class="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </button>
                
                <div 
                  v-if="openDropdowns[sample.hashid]" 
                  class="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-50"
                  @mousedown.prevent
                >
                  <div class="py-1">
                    <inertia-link
                      :href="route('app.task.edit-record', sample.hashid)"
                      class="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    >
                      <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </inertia-link>
                    <button
                      v-if="['admin', 'consultant', 'consultant_user', 'contractor', 'lab_user'].includes($page.props.auth.user.role)"
                      @click="confirmDelete(sample.hashid); closeSampleDropdown(sample.hashid)"
                      class="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-800"
                    >
                      <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
        </table>
      </div>
    </div>
  </div>

  <div v-if="samples.length === 0 && newSamples.length === 0" class="bg-white px-6 py-4 my-4">
    <div class="text-center py-8">
      <h3 class="text-lg font-medium text-gray-900 mb-2">No samples found</h3>
      <p v-if="canCreateSamples" class="text-gray-600 mb-4">Get started by adding your first sample to this task.</p>
      <p v-else class="text-gray-600 mb-4">Samples will appear here once they are created by an authorized user (consultant, contractor, or soil sampler).</p>
      <button 
        v-if="canCreateSamples"
        @click="startAddingSamples(1)"
        class="px-4 py-2 text-sm font-medium text-white bg-navy rounded-md hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy"
      >
        Add First Sample
      </button>
    </div>
  </div>

  <!-- Lab Test Info Modal -->
  <div v-if="showLabTestInfo" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="showLabTestInfo = false">
    <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 xl:w-2/5 shadow-lg rounded-md bg-white" @click.stop>
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-gray-900">Available Lab Tests</h3>
        <button
          @click="showLabTestInfo = false"
          class="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
          type="button"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      <!-- Lab Selection Dropdown -->
      <div class="mb-4" v-if="availableLabs.length > 1">
        <label for="lab-select" class="block text-sm font-medium text-gray-700 mb-2">
          Select Laboratory:
        </label>
        <select 
          id="lab-select"
          v-model="selectedLabId" 
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option v-for="lab in availableLabs" :key="lab.id" :value="lab.id">
            {{ lab.name }}
          </option>
        </select>
       
      </div>
      
      <!-- Current Lab Display -->
      <div class="mb-4" v-else>
        <div class="text-sm text-gray-600">
          Laboratory: <span class="font-medium text-gray-900">{{ selectedLabName }}</span>
        </div>

      </div>
      
      <div class="max-h-96 overflow-y-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                Test Name
              </th>
              <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/3">
                Description
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="test in modalLabTests" :key="test.code" class="hover:bg-gray-50">
              <td class="px-4 py-3 text-sm font-medium text-gray-900">
                {{ test.name }}
              </td>
              <td class="px-4 py-3 text-sm text-gray-500">
                {{ test.description || 'No description available' }}
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!modalLabTests || modalLabTests.length === 0" class="text-center py-8 text-gray-500">
          No lab tests available for {{ selectedLabName }}
        </div>
      </div>
    </div>
  </div>

  <!-- Georeference Info Modal -->
  <div v-if="showGeoreferenceInfo" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="showGeoreferenceInfo = false">
    <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 xl:w-2/5 shadow-lg rounded-md bg-white" @click.stop>
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-gray-900">Georeference Legal Requirements</h3>
        <button
          @click="showGeoreferenceInfo = false"
          class="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
          type="button"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      <div class="space-y-4 text-sm text-gray-700">
        <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h4 class="text-sm font-medium text-yellow-800">Irish Legal Requirement (SI 42 of 2025)</h4>
            </div>
          </div>
        </div>
        
        <div>
          <p class="font-medium mb-2">Under SI 42 of 2025, soil analysis results issued after the 14th of September 2025 must state:</p>
          <ul class="list-disc list-inside space-y-1 ml-4">
            <li>The corresponding geo-reference, OR</li>
            <li>Land Parcel Identification System (LPIS) parcel(s) represented by the soil sample</li>
          </ul>
        </div>
        
        <div class="bg-red-50 border border-red-200 rounded p-3">
          <p class="font-medium text-red-800 mb-1">Important:</p>
          <p class="text-red-700">Soil test results issued after 14th September 2025 that do not state the corresponding geo-reference or LPIS parcel(s) will be defaulted to <strong>P Index 4</strong>.</p>
        </div>
        
        <div>
          <p class="font-medium mb-2">Definition:</p>
          <p>"Geo-reference" means having Global Positioning System (GPS) co-ordinates linked to that soil sample.</p>
        </div>
        
        <div>
          <p class="font-medium mb-2">Multiple Parcels:</p>
          <p>Where a soil sample represents multiple small parcels, the LPIS number or geo-reference for each parcel must be stated on the report.</p>
        </div>
        
        <div class="bg-blue-50 border border-blue-200 rounded p-3">
          <p class="font-medium text-blue-800 mb-2">Livefarm Compliance Solutions:</p>
          <div class="text-blue-700 space-y-2">
            <p><strong>Scout+ Mobile App:</strong> Samples collected with Livefarm Scout+ mobile app automatically collect GPS co-ordinates in the field, which are included with all orders sent to the lab.</p>
            
            <p><strong>Manual Sample Entry:</strong> For manually input samples, we offer 2 options:</p>
            <ul class="list-disc list-inside ml-4 space-y-1">
              <li>If the farm is mapped in Livefarm, select a parcel on the map and Livefarm will use the parcel center-point as the official georeference</li>
              <li>Alternatively and/or in addition, record the relevant LPIS number(s) for the sample</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Organic Matter Info Modal -->
  <div v-if="showOmInfo" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="showOmInfo = false">
    <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 xl:w-2/5 shadow-lg rounded-md bg-white" @click.stop>
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-gray-900">Organic Matter (OM) Information</h3>
        <button
          @click="showOmInfo = false"
          class="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
          type="button"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      <div class="space-y-4">
        <div class="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 class="font-medium text-red-800 mb-2">🔴 EU CAP Compliance Threshold</h4>
          <p class="text-red-700 text-sm">
            <strong>Under 3.4%:</strong> Non-compliant with GAEC (Good Agricultural and Environmental Condition) standards. Remedial action may be required to maintain eligibility for Single Farm Payment.
          </p>
        </div>
        
        <div class="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h4 class="font-medium text-orange-800 mb-2">🟠 Compliant but Low</h4>
          <p class="text-orange-700 text-sm">
            <strong>3.4-5%:</strong> Meets compliance requirements but typical for mineral soils. Consider practices to improve organic matter levels.
          </p>
        </div>
        
        <div class="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 class="font-medium text-green-800 mb-2">🟢 Good Levels</h4>
          <p class="text-green-700 text-sm">
            <strong>5-10%:</strong> Good organic matter levels for mineral soils. Supports healthy soil structure and fertility.
          </p>
        </div>
        
        <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 class="font-medium text-amber-800 mb-2">🟤 High Levels</h4>
          <p class="text-amber-700 text-sm">
            <strong>10-20%:</strong> High organic matter levels for mineral soils. Excellent for soil health and carbon sequestration.
          </p>
        </div>
        
        <div class="bg-amber-100 border border-amber-300 rounded-lg p-4">
          <h4 class="font-medium text-amber-900 mb-2">🟤 Organic/Peat Soils</h4>
          <p class="text-amber-800 text-sm">
            <strong>Over 20%:</strong> Typical range for organic or peat soils. Requires specialized management practices.
          </p>
        </div>
        
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 class="font-medium text-blue-800 mb-2">📋 Key Information</h4>
          <ul class="list-disc list-inside text-blue-700 text-sm space-y-1">
            <li>Organic matter gives soil its brown color and improves structure</li>
            <li>Higher levels support better water retention and nutrient cycling</li>
            <li>EU regulations require maintaining OM levels above 3.4%</li>
            <li>Different soil types have different typical ranges</li>
          </ul>
        </div>
      </div>
      
      <div class="mt-6 flex justify-end">
        <button
          @click="showOmInfo = false"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Got it
        </button>
      </div>
    </div>
  </div>

  <!-- Coordinates Display Modal -->
  <div v-if="showCoordinatesModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="closeCoordinatesModal">
    <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 xl:w-2/5 shadow-lg rounded-md bg-white" @click.stop>
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-gray-900">
          {{ selectedSampleForCoordinates?.sample_id || selectedSampleForCoordinates?.id }} - Coordinates
        </h3>
        <button
          @click="closeCoordinatesModal"
          class="text-gray-400 hover:text-gray-600 focus:outline-none"
          type="button"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      <div v-if="selectedSampleForCoordinates" class="max-h-96 overflow-y-auto">
        <div class="mb-4">
          <button
            @click="copyAllCoordinates"
            class="w-full px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Copy All as Table
          </button>
        </div>
        
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Point</th>
              <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Latitude</th>
              <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Longitude</th>
              <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="coordinate in getGeoreferenceCoordinates(selectedSampleForCoordinates)" :key="coordinate.index" class="hover:bg-gray-50">
              <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{{ coordinate.index }}</td>
              <td class="px-3 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{{ coordinate.lat }}</td>
              <td class="px-3 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{{ coordinate.lng }}</td>
              <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                      :class="coordinate.type === 'GPS' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'">
                  {{ coordinate.type }}
                </span>
              </td>
              <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                <button
                  @click="copyCoordinate(coordinate)"
                  class="text-blue-600 hover:text-blue-900 focus:outline-none"
                  title="Copy coordinate"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        
        <div v-if="!getGeoreferenceCoordinates(selectedSampleForCoordinates) || getGeoreferenceCoordinates(selectedSampleForCoordinates).length === 0" class="text-center py-8 text-gray-500">
          No coordinates available for this sample.
        </div>
      </div>
    </div>
  </div>

  <!-- Comment Modal -->
  <div v-if="showCommentModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="closeCommentModal">
    <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-gray-900">Edit Comment</h3>
        <button
          @click="closeCommentModal"
          class="text-gray-400 hover:text-gray-600 focus:outline-none"
          type="button"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">Comment:</label>
        <textarea
          v-model="tempCommentValue"
          rows="3"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter sample comment..."
          @keydown.enter.prevent
        ></textarea>
      </div>
      
      <div class="flex justify-end space-x-3">
        <button
          @click="closeCommentModal"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          @click="saveComment"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Save
        </button>
      </div>
    </div>
  </div>

  <!-- LPIS Modal -->
  <div v-if="showLpisModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="closeLpisModal">
    <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-gray-900">Edit LPIS</h3>
        <button
          @click="closeLpisModal"
          class="text-gray-400 hover:text-gray-600 focus:outline-none"
          type="button"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">LPIS Number:</label>
        <input
          v-model="tempLpisValue"
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter LPIS number..."
          @keydown.enter="saveLpis"
        />
        <p class="mt-1 text-xs text-gray-500">Land Parcel Identification System number (optional)</p>
      </div>
      
      <div class="flex justify-end space-x-3">
        <button
          @click="closeLpisModal"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          @click="saveLpis"
          class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          Save
        </button>
      </div>
    </div>
  </div>

  <!-- Marker editing now happens directly on the main map - no modal needed -->
  <div v-if="false" class="hidden">
    <div class="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow-lg rounded-md bg-white" @click.stop>
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-gray-900">
          Edit Georeference Markers - {{ editingMarkerSample?.sample_id || editingMarkerSample?.id }}
        </h3>
        <button
          @click="closeMarkerEditModal"
          class="text-gray-400 hover:text-gray-600 focus:outline-none"
          type="button"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      <div class="mb-4 p-3 bg-blue-50 rounded-md">
        <p class="text-sm text-blue-800">
          <svg class="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
          Select GPS coordinates from the sample path to use as georeference markers. These will be used for lab compliance and reporting.
        </p>
      </div>

      <!-- Interactive Map for Marker Selection -->
      <div v-if="editingMarkerSample && editingMarkerSample.paths_lat_lon" class="border border-gray-300 rounded-lg">
        <!-- Map Instructions -->
        <div class="p-3 bg-gray-50 border-b border-gray-200">
          <div class="flex items-center justify-between text-sm">
            <div class="flex items-center space-x-4">
              <span class="text-gray-700">
                <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                Click on GPS path points to select/deselect markers
              </span>
              <div class="flex items-center space-x-3">
                <div class="flex items-center">
                  <div class="w-3 h-3 bg-blue-500 rounded-full mr-1"></div>
                  <span class="text-xs text-gray-600">GPS Path</span>
                </div>
                <div class="flex items-center">
                  <div class="w-3 h-3 bg-green-500 rounded-full mr-1"></div>
                  <span class="text-xs text-gray-600">Selected Marker</span>
                </div>
                <div class="flex items-center">
                  <div class="w-3 h-3 bg-gray-400 rounded-full mr-1"></div>
                  <span class="text-xs text-gray-600">Unselected Point</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- ArcGIS Map Container -->
        <div class="relative">
          <div 
            ref="markerMapContainer"
            id="markerMapContainer"
            class="h-96 w-full"
            :class="{ 'opacity-50 pointer-events-none': isLoadingMarkerMap }"
          ></div>
          
          <!-- Loading Overlay -->
          <div v-if="isLoadingMarkerMap" class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <div class="flex items-center space-x-2">
              <svg class="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="text-sm text-gray-600">Loading interactive map...</span>
            </div>
          </div>
        </div>
        
        <!-- Selected Points Summary -->
        <div class="p-3 bg-gray-50 border-t border-gray-200">
          <div class="flex items-center justify-between text-sm">
            <span class="text-gray-700">
              <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                </svg>
              Points selected: <strong>{{ tempMarkerIndices.length }}</strong> of {{ getPathCoordinates(editingMarkerSample).length }}
            </span>
            <div class="flex items-center space-x-1">
              <span v-if="tempMarkerIndices.length > 0" class="text-xs text-gray-500">
                Indices: {{ tempMarkerIndices.sort((a, b) => a - b).join(', ') }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-between items-center mt-6">
        <div class="text-sm text-gray-600">
          {{ tempMarkerIndices.length }} marker{{ tempMarkerIndices.length !== 1 ? 's' : '' }} selected
        </div>
        <div class="flex space-x-3">
          <button
            @click="selectAllMarkersOnMap"
            class="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Select All
          </button>
          <button
            @click="clearAllMarkersOnMap"
            class="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Clear All
          </button>
          <button
            @click="closeMarkerEditModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            @click="saveMarkerChanges"
            :disabled="isUpdatingMarkers"
            class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <span v-if="isUpdatingMarkers" class="flex items-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
            <span v-else>Save Markers</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from "@vueform/multiselect";
import { useToast } from "vue-toastification";

// Note: ArcGIS components are handled by the main FarmMap component

// Columns written by the CSV export, in the same order as the table headers.
// All of them are exported every time, whatever the table is currently showing.
// The key matches a columnVisibility key; the label matches the <th> text.
// The two trailing table columns (locate on map, row actions) carry no data and
// are deliberately absent.
const EXPORT_COLUMNS = [
  { key: 'sampleId', label: 'Sample ID' },
  { key: 'test', label: 'Test' },
  { key: 'reference', label: 'Ref.' },
  { key: 'soilType', label: 'Soil Type' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'farmName', label: 'Farm' },
  { key: 'parcelNo', label: 'Parcel No' },
  { key: 'parcelName', label: 'Parcel Name' },
  { key: 'comment', label: 'Comment' },
  { key: 'lpis', label: 'LPIS' },
  { key: 'collected', label: 'Collected' },
  { key: 'georef', label: 'Georeference' },
  { key: 'result', label: 'Result' },
  { key: 'labPh', label: 'pH' },
  { key: 'labLimeReq', label: 'Lime Req' },
  { key: 'labPhosphorus', label: 'P (mg/l)' },
  { key: 'labPIndexGrassland', label: 'P Index (Grass)' },
  { key: 'labPIndexOther', label: 'P Index (Other)' },
  { key: 'labPotassium', label: 'K (mg/l)' },
  { key: 'labKIndexMineral', label: 'K Index (Mineral)' },
  { key: 'labKIndexPeat', label: 'K Index (Peat)' },
  { key: 'labMagnesium', label: 'Mg' },
  { key: 'labCalcium', label: 'Ca' },
  { key: 'labCopper', label: 'Cu' },
  { key: 'labManganese', label: 'Mn' },
  { key: 'labZinc', label: 'Zn' },
  { key: 'labBoron', label: 'B' },
  { key: 'labMagnesiumStatus', label: 'Mg Status' },
  { key: 'labCalciumStatus', label: 'Ca Status' },
  { key: 'labCopperStatus', label: 'Cu Status' },
  { key: 'labManganeseStatus', label: 'Mn Status' },
  { key: 'labZincStatus', label: 'Zn Status' },
  { key: 'labBoronStatus', label: 'B Status' },
  { key: 'labOm', label: 'OM' },
  { key: 'labTestDate', label: 'Test Date' },
];

export default {
  components: {
    Multiselect,
  },
  emits: ['testSentToLab'],
  props: {
    task: Object,
    tests: Array,
    farmer_id: String,
    samples: Object,
    farmteam_task_number: String,
    availableLabTests: Array,
    currentTaskLabId: [String, Number],
    currentTab: String,
    labUpdateChanges: Object,
    labUpdateNeeded: Boolean,
  },

  data() {
    return {
      // Consistent georeference color across the application
      GEOREFERENCE_COLOR: '#DC2626',
      isCreating: false,
      showGeoreferenceError: false,
      isSelectingGeoreferences: false,
      newSamples: [], // Array of sample objects being created
      currentSampleIndex: 0, // For georeference selection
      isProcessingLabOrder: false,
      labOrderBannerDismissed: false,
      showLabOrderBannerAfterSave: false,
      georeferenceUpdateTimer: null,
      parcelSelectionMode: 'manual', // 'manual' or 'parcel'
      parcelsAvailable: false,
      showActionsMenu: false,
      showAddSubmenu: false,
      showAssignSubmenu: false,
      showSortSubmenu: false,
      sortField: 'collected_date', // Default sort field
      sortDirection: 'asc', // Default sort direction (newest first)
      openDropdowns: {}, // Track which sample action dropdowns are open
      showLabTestInfo: false, // Track lab test info popup visibility
      showGeoreferenceInfo: false, // Track georeference info popup visibility
      showOmInfo: false, // Track organic matter info popup visibility
      selectedLabId: this.currentTaskLabId || null, // Initialize with current task lab ID
      // Column visibility controls
      columnVisibility: null, // Will be initialized in mounted()
      showColumnControls: false,
      showSortOptions: false,
      sampleIdSearch: '', // Search term for Sample ID filtering
      farmFilter: [], // Array of selected farm names for filtering
      showFarmFilterDropdown: false, // Control farm filter dropdown visibility
      dataType: 'overview', // 'overview', 'major', or 'micro'
      viewType: 'interpreted', // 'interpreted' (indices/status) or 'values'
      // Coordinate display modal
      showCoordinatesModal: false,
      selectedSampleForCoordinates: null,
      // Comment and LPIS modals
      showCommentModal: false,
      showLpisModal: false,
      editingSampleIndex: null,
      tempCommentValue: '',
      tempLpisValue: '',
      // Marker editing for SMAP samples - map-based approach
      isEditingMarkers: false, // Track if we're in marker editing mode
      editingMarkerSample: null, // The sample being edited
      tempMarkerIndices: [], // Temporarily selected marker indices
      isUpdatingMarkers: false, // Track saving state
      // Store original column visibility for restoration
      originalColumnVisibility: null
    };
  },

  created() {
    // Ensure all arrays are properly initialized
    this.ensureArraysInitialized();
  },

  mounted() {
    // Initialize toast system
    this.$toast = useToast();
    
    // Initialize column visibility FIRST to prevent null reference errors
    this.columnVisibility = this.getDefaultColumnVisibility();
    
    // Set initial view to overview
    this.dataType = 'overview';
    this.updateLabResultsView();
    
    // Initialize selected lab ID to current task lab
    if (this.currentTaskLabId) {
      this.selectedLabId = this.currentTaskLabId;
    }
    
     
    if (!this.selectedLabId && this.availableLabTests && this.availableLabTests.length > 0) {
      // If no currentTaskLabId, default to first available lab
      this.selectedLabId = this.availableLabTests[0].lab_id;
      console.log('No currentTaskLabId, defaulted to first lab:', this.selectedLabId);
    }
    
    // Close dropdowns when clicking outside
    this.handleDocumentClick = (e) => {
      if (!e.target.closest('.relative')) {
        this.showActionsMenu = false;
        this.showAddSubmenu = false;
        this.showAssignSubmenu = false;
        this.showColumnControls = false;
        this.showSortOptions = false;
        this.showFarmFilterDropdown = false;
        // Close all sample dropdowns
        this.openDropdowns = {};
      }
      // Close lab test info popup when clicking outside (but not on the info button or modal)
      if (this.showLabTestInfo && !e.target.closest('.fixed') && !e.target.closest('button[title="View test descriptions"]')) {
        this.showLabTestInfo = false;
      }
      // Close georeference info popup when clicking outside (but not on the info button or modal)
      if (this.showGeoreferenceInfo && !e.target.closest('.fixed') && !e.target.closest('button[title="View georeference legal requirements"]')) {
        this.showGeoreferenceInfo = false;
      }
    };
    document.addEventListener('click', this.handleDocumentClick);
    
    // Listen for map click events for georeference selection
    this.$emitter.on("argicsMapLocation", this.handleGeoreferenceMapClick);
    
    // Listen for GPS marker selection clicks
    this.$emitter.on("gpsMarkerClick", this.handleGPSMarkerClick);
    
    // Listen for parcel unavailable events
    this.$emitter.on("parcelUnavailable", this.handleParcelUnavailable);
    
    // Display existing sample georeferences on the map with delay to ensure map is ready
    setTimeout(() => {
      this.displayExistingSampleGeoreferences();
    }, 1500); // Wait for map initialization
    
    // Also retry after a longer delay in case the first attempt fails
    setTimeout(() => {
      this.displayExistingSampleGeoreferences();
    }, 3000);
    
    // Add window resize listener to update column visibility for screen size changes
    this.handleResize = () => {
      // Only reset to defaults if user hasn't saved custom preferences
      const savedPreferences = localStorage.getItem('sample-table-column-visibility');
      if (!savedPreferences) {
        this.columnVisibility = this.getDefaultColumnVisibility();
      }
    };
    window.addEventListener('resize', this.handleResize);

  },

  beforeUnmount() {
    // Clean up event listeners
    this.$emitter.off("argicsMapLocation", this.handleGeoreferenceMapClick);
    this.$emitter.off("gpsMarkerClick", this.handleGPSMarkerClick);
    this.$emitter.off("parcelUnavailable", this.handleParcelUnavailable);
    
    // Clean up document click listener
    document.removeEventListener('click', this.handleDocumentClick);
    
    // Clean up window resize listener
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
    
    // Clean up any pending timers
    if (this.georeferenceUpdateTimer) {
      clearTimeout(this.georeferenceUpdateTimer);
    }
  },

  watch: {
    samples: {
      handler(newSamples, oldSamples) {
        //console.log('Samples watcher triggered:', newSamples ? newSamples.length : 0);
        
        // Only re-display if samples actually changed (avoid redundant calls)
        if (newSamples && Array.isArray(newSamples)) {
          const samplesChanged = !oldSamples || 
                                oldSamples.length !== newSamples.length ||
                                (oldSamples.length > 0 && newSamples.length > 0 &&
                                 oldSamples.map(s => s.id).join(',') !== newSamples.map(s => s.id).join(','));
          
          if (samplesChanged) {
            //console.log('Samples actually changed, updating georeferences display');
            
            // Clear any existing timer to debounce rapid updates
            if (this.georeferenceUpdateTimer) {
              clearTimeout(this.georeferenceUpdateTimer);
            }
            
            // Debounced update to avoid rapid-fire calls
            this.georeferenceUpdateTimer = setTimeout(() => {
              this.displayExistingSampleGeoreferences();
              this.georeferenceUpdateTimer = null;
            }, 750); // Slightly longer delay to ensure map is ready
          } else {
            //console.log('Samples data unchanged, skipping georeference update');
          }
        }
      },
      immediate: true // Run immediately when component is created
    },

    currentTaskLabId: {
      handler(newLabId) {
        // Ensure selectedLabId is set when currentTaskLabId is available
        if (newLabId && !this.selectedLabId) {
          this.selectedLabId = newLabId;
        }
      },
      immediate: true
    },
    
    tempMarkerIndices: {
      handler(newIndices) {
        // Update map visual appearance when marker selection changes programmatically
        if (this.markerPointGraphics && this.markerPointGraphics.length > 0) {
          this.markerPointGraphics.forEach((graphic, index) => {
            const isSelected = newIndices.includes(index);
            this.updatePointAppearance(graphic, isSelected);
          });
        }
      },
      deep: true
    }
  },

  computed: {
    filteredAndSortedSamples() {
      if (!this.samples || this.samples.length === 0) {
        return [];
      }

      let filtered = [...this.samples];
      
      // Apply Sample ID filter if search term exists
      if (this.sampleIdSearch && this.sampleIdSearch.trim()) {
        const searchTerm = this.sampleIdSearch.trim().toLowerCase();
        filtered = filtered.filter(sample => {
          const sampleId = sample.sample_id || sample.id || '';
          return String(sampleId).toLowerCase().includes(searchTerm);
        });
      }
      
      // Apply farm filter if farms are selected
      if (this.farmFilter && this.farmFilter.length > 0) {
        filtered = filtered.filter(sample => {
          const farmName = sample.farm_name || '';
          
          // Check if unassigned filter is selected
          const includesUnassigned = this.farmFilter.includes('__UNASSIGNED__');
          
          // If sample has no farm name and unassigned is selected, include it
          if (!farmName.trim() && includesUnassigned) {
            return true;
          }
          
          // If sample has a farm name, check if it's in the selected farms
          if (farmName.trim()) {
            return this.farmFilter.includes(farmName);
          }
          
          // Sample has no farm name but unassigned is not selected
          return false;
        });
      }
      
      // Apply sorting
      return filtered.sort((a, b) => {
        let aValue, bValue;
        
        if (this.sortField === 'sample_id') {
          aValue = a.sample_id || '';
          bValue = b.sample_id || '';
        } else if (this.sortField === 'collected_date') {
          // For SMAP samples, use sample_date_end; for manual samples, use created_at
          aValue = this.getCollectedDateValue(a);
          bValue = this.getCollectedDateValue(b);
        }
        
        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';
        
        // Convert to strings for comparison if needed
        if (typeof aValue !== 'string') aValue = String(aValue);
        if (typeof bValue !== 'string') bValue = String(bValue);
        
        const comparison = aValue.localeCompare(bValue, undefined, { numeric: true });
        return this.sortDirection === 'asc' ? comparison : -comparison;
      });
    },

    // Legacy computed property for backward compatibility
    sortedSamples() {
      return this.filteredAndSortedSamples;
    },

    // Every column, in header order. The export deliberately ignores the column
    // picker and the view-mode toggles: dropping unwanted columns in a
    // spreadsheet is easier than setting the screen up correctly first.
    exportColumns() {
      return EXPORT_COLUMNS;
    },

    transformedLabTests() {
      if (!this.availableLabTests || !Array.isArray(this.availableLabTests) || !this.currentTaskLabId) {
        return {};
      }
      
      // Get tests from the current task's lab
      const currentTaskLab = this.availableLabTests.find(lab => lab.lab_id == this.currentTaskLabId);
      if (!currentTaskLab || !currentTaskLab.tests) {
        return {};
      }
      
      const transformed = {};
      currentTaskLab.tests.forEach(test => {
        if (test.code && test.name) {
          transformed[test.code] = test.name;
        }
      });
      return transformed;
    },

    // Get tests for the info modal based on selected lab
    modalLabTests() {
      if (!this.availableLabTests || !Array.isArray(this.availableLabTests)) {
        return [];
      }
      
      const selectedLab = this.availableLabTests.find(lab => lab.lab_id == this.selectedLabId);
      return selectedLab ? selectedLab.tests : [];
    },

    // Get available labs for dropdown
    availableLabs() {
      if (!this.availableLabTests || !Array.isArray(this.availableLabTests)) {
        return [];
      }
      
      return this.availableLabTests.map(lab => ({
        id: lab.lab_id,
        name: lab.lab_name
      }));
    },

    // Get currently selected lab name
    selectedLabName() {
      if (!this.availableLabTests || !Array.isArray(this.availableLabTests)) {
        return 'Unknown Lab';
      }
      const selectedLab = this.availableLabTests.find(lab => lab.lab_id == this.selectedLabId);
      return selectedLab ? selectedLab.lab_name : 'Unknown Lab';
    },

    // Get unique farm names for filter options
    availableFarms() {
      if (!this.samples || !Array.isArray(this.samples)) {
        return [];
      }
      
      // Get all farm names including null/undefined ones
      const allFarmNames = this.samples.map(sample => sample.farm_name);
      
      // Check if we have any samples without farm assignments
      const hasUnassigned = allFarmNames.some(name => !name || name.trim() === '');
      
      // Get unique non-empty farm names
      const farmNames = [...new Set(allFarmNames.filter(name => name && name.trim() !== ''))].sort();
      
      const options = farmNames.map(name => ({
        value: name,
        label: name
      }));
      
      // Add "Unassigned" option if there are samples without farms
      if (hasUnassigned) {
        options.unshift({
          value: '__UNASSIGNED__',
          label: 'Unassigned'
        });
      }
      
      return options;
    },



    // Count valid samples that can be saved
    validSampleCount() {
      return this.newSamples.filter((sample, index) => this.canSaveNewSampleAtIndex(index)).length;
    },

    // Check if ALL samples can be saved (not just any)
    canSaveAnySample() {
      // Require ALL samples to be valid, not just some
      return this.newSamples.length > 0 && 
             this.newSamples.every((sample, index) => this.canSaveNewSampleAtIndex(index));
    },

    // Check if we're adding samples
    isAddingNewSample() {
      return this.newSamples.length > 0;
    },

    // Check if this is a manual task (no mobile app collection)
    isManualTask() {
      // Check if task has collection_method property
      if (this.task && this.task.collection_method) {
        return this.task.collection_method !== 'mobile';
      }
      
      // Fallback: Check if any samples have mobile data (paths_xy)
      if (this.samples && this.samples.length > 0) {
        const hasMobileData = this.samples.some(sample => 
          sample.paths_xy && Array.isArray(sample.paths_xy) && sample.paths_xy.length > 0
        );
        return !hasMobileData;
      }
      
      // Default to manual if we can't determine
      return true;
    },

    // Check if this is a manual task (no mobile app collection)
    isManualTask() {
      // Check if task has collection_method property
      if (this.task && this.task.collection_method) {
        return this.task.collection_method !== 'mobile';
      }
      
      // Fallback: Check if any samples have mobile data (paths_xy)
      if (this.samples && this.samples.length > 0) {
        const hasMobileData = this.samples.some(sample => 
          sample.paths_xy && Array.isArray(sample.paths_xy) && sample.paths_xy.length > 0
        );
        return !hasMobileData;
      }
      
      // Default to manual if we can't determine
      return true;
    },

    // Get all parcel IDs that are already used by existing samples or samples in current session
    usedParcelIds() {
      const usedIds = new Set();
      
      // Add parcel IDs from existing saved samples
      if (this.samples && Array.isArray(this.samples)) {
        this.samples.forEach(sample => {
          if (sample.farm_location && sample.farm_location.parcel_hashid) {
            usedIds.add(String(sample.farm_location.parcel_hashid));
          }
        });
      }
      
      // Add parcel IDs from samples currently being edited (excluding current sample)
      this.newSamples.forEach((sample, index) => {
        if (sample.parcelInfo && sample.parcelInfo.hashid && index !== this.currentSampleIndex) {
          usedIds.add(String(sample.parcelInfo.hashid));
        }
      });
      
      return Array.from(usedIds);
    },

    // Check if parcels are available for selection
    hasAvailableParcels() {
      if (!this.$root.farmer || !this.$root.farmer.farms) {
        return false;
      }
      
      // Count total parcels across all farms
      const totalParcels = this.$root.farmer.farms.reduce((count, farm) => {
        return count + (farm.parcels ? farm.parcels.length : 0);
      }, 0);
      
      return totalParcels > 0;
    },

    // Generate suggested sample ID based on existing samples
    suggestedSampleId() {
      const allIds = [
        ...this.samples.map(s => s.id),
        ...this.newSamples.map(s => s.sample_id).filter(id => id.trim())
      ];
      
      if (allIds.length === 0) {
        return '1';
      }
      
      // Try to find numeric pattern
      const numericIds = allIds
        .map(id => {
          // Extract numbers from the ID
          const match = id.toString().match(/(\d+)$/);
          return match ? parseInt(match[1]) : null;
        })
        .filter(num => num !== null);
      
      if (numericIds.length > 0) {
        const maxNum = Math.max(...numericIds);
        
        // Check if the highest ID follows a pure numeric pattern
        const highestId = allIds.find(id => id.toString().endsWith(maxNum.toString()));
        if (highestId && /^\d+$/.test(highestId.toString())) {
          // Pure numeric - just increment
          return (maxNum + 1).toString();
        } else if (highestId) {
          // Has prefix - maintain the pattern
          const prefix = highestId.toString().replace(/\d+$/, '');
          return prefix + (maxNum + 1);
        }
      }
      
      // Fallback: suggest next number
      return (allIds.length + 1).toString();
    },

    // Lab order banner logic
    shouldShowLabOrderBanner() {
      // Don't show if manually dismissed
      if (this.labOrderBannerDismissed) return false;
      
      // Don't show if no samples or tests
      if (!this.samples || this.samples.length === 0 || !this.tests || this.tests.length === 0) return false;
      
      // Don't show if no tests are in 'collected' status
      const hasCollectedTests = this.tests.some(test => test.status?.toLowerCase() === 'collected');
      if (!hasCollectedTests) return false;
      
      // Show if explicitly requested after saving samples
      if (this.showLabOrderBannerAfterSave) return true;
      
      // Show if there are tracked changes that need to be sent to lab
      if (this.labUpdateNeeded && this.labUpdateChanges && this.labUpdateChanges.total_changes > 0) {
        return true;
      }
      
      // Show if there are samples but no lab order has been sent
      const hasUnsent = this.tests.some(test => !test.sent_to_lab);
      return hasUnsent && this.samples.length > 0;
    },

    labOrderBannerTitle() {
      // If we have specific changes to report, show as update
      if (this.labUpdateNeeded && this.labUpdateChanges && this.labUpdateChanges.total_changes > 0) {
        return "Lab Order Update Required";
      }
      
      const hasAnySent = this.tests.some(test => test.sent_to_lab);
      return hasAnySent ? "Lab Order Update Available" : "Ready to Send Lab Order";
    },

    labOrderBannerMessage() {
      // If we have specific changes to display, show those
      if (this.labUpdateNeeded && this.labUpdateChanges && this.labUpdateChanges.total_changes > 0) {
        const changes = this.labUpdateChanges.changes_detail;
        const messages = [];
        
        // Check for new samples
        if (changes.new_samples) {
          messages.push(changes.new_samples.message);
        }
        
        // Check for modified samples
        if (changes.modified_samples) {
          messages.push(changes.modified_samples.message);
        }
        
        return messages.join(', ') + '. Send update to lab?';
      }
      
      const unsentTests = this.tests.filter(test => !test.sent_to_lab);
      const sentTests = this.tests.filter(test => test.sent_to_lab);
      
      if (unsentTests.length > 0 && sentTests.length === 0) {
        // No orders sent yet
        const sampleCount = this.samples.length;
        const testCount = unsentTests.length;
        return `${sampleCount} sample${sampleCount !== 1 ? 's' : ''} ready to send to ${unsentTests[0].lab_name}${testCount > 1 ? ` (${testCount} test${testCount !== 1 ? 's' : ''})` : ''}.`;
      } else if (unsentTests.length > 0 && sentTests.length > 0) {
        // Some orders sent, some not
        return `${unsentTests.length} additional test${unsentTests.length !== 1 ? 's' : ''} ready to send to lab.`;
      } else {
        // All sent, but showing due to recent activity
        return `Lab orders have been sent. Send updates if samples have changed.`;
      }
    },

    labOrderBannerAction() {
      const hasAnySent = this.tests.some(test => test.sent_to_lab);
      return hasAnySent ? "Send Update to Lab" : "Send Order to Lab";
    },

    // Check if current user can create manual samples
    canCreateSamples() {
      const user = this.$page.props.auth.user;
      const task = this.task;

      if (!user || !task) return false;

      // Admin can always create samples
      if (user.role === 'admin') return true;

      // Consultants can create samples for tasks in their organization
      if ((user.role === 'consultant' || user.role === 'consultant_user') &&
          user.organisation_id === task.organisation_id) {
          return true;
      }

      // Contractors can create samples for tasks assigned to them
      if (user.role === 'contractor' && user.id === task.assigned_to) {
          return true;
      }

      // Soil samplers can create samples for tasks assigned to them
      if (user.role === 'soil_sampler' && task.assigned_to === user.id) {
          return true;
      }

      // Farm owners and other roles cannot create manual samples
      return false;
    },

    // Count visible columns
    visibleColumnCount() {
      if (!this.columnVisibility) return 0;
      return Object.values(this.columnVisibility).filter(visible => visible).length;
    },

    // Count total available columns
    totalColumnCount() {
      if (!this.columnVisibility) return 0;
      return Object.keys(this.columnVisibility).length;
    },

  },



  methods: {
    // Soil test styling helpers
    getSoilTestPillStyle(colour) {
      if (!colour) return {};
      
      return {
        backgroundColor: `rgb(${colour})`,
        color: 'white',
        padding: '2px 6px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '500',
        display: 'inline-block',
        minWidth: '20px',
        textAlign: 'center'
      };
    },

    formatSoilTestValue(value, decimals = 1) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (Number.isNaN(num)) return '-';
      return num.toFixed(decimals);
    },

    abbreviateStatus(status) {
      if (!status) return '-';
      const abbreviations = {
        'Very Low': 'VL',
        'Low': 'L',
        'Normal': 'N',
        'High': 'H',
        'Very High': 'VH'
      };
      return abbreviations[status] || status;
    },

    // CSV export of the records table, exactly as currently displayed
    getAssignedStatusText(sample) {
      // Mirrors the coloured dot in the Assigned column, including the two
      // cases where that cell is deliberately blank.
      if (this.getParcelNumber(sample)) {
        if (this.task && this.task.organisation_name == 'ISEC') {
          if ((sample.matched && sample.zone_issues && sample.lpis_issue) || (!sample.matched && sample.lpis_issue)) {
            return 'Red';
          }
          if ((sample.matched && (sample.zone_issues || sample.lpis_issue) && sample.zone_issues != sample.lpis_issue) || (!sample.matched && !sample.lpis_issue)) {
            return 'Amber';
          }
          if (sample.matched && !sample.zone_issues && !sample.lpis_issue) {
            return 'Green';
          }
          return '';
        }
        if (sample.matched) {
          return sample.zone_issues ? 'Amber' : 'Green';
        }
        return '';
      }
      return sample.matched ? '' : '-';
    },

    getCellValue(sample, key) {
      switch (key) {
        case 'sampleId': return sample.id;
        case 'test': return this.getLabTestsDisplay(sample);
        case 'reference': return sample.sample_reference || '-';
        case 'soilType': return sample.soil_type || '-';
        case 'assigned': return this.getAssignedStatusText(sample);
        case 'farmName': return sample.farm_name;
        case 'parcelNo': return this.getParcelNumber(sample) || '-';
        case 'parcelName': return this.getParcelName(sample) || '-';
        case 'comment': return sample.comment || '-';
        case 'lpis': return sample.lpis || '-';
        case 'collected': return this.formatCollectedDate(sample);
        case 'georef': return this.getGeoreferenceCount(sample);
        case 'result': return sample.lab_results ? 'Yes' : 'No';
        case 'labPh': return this.formatSoilTestValue(sample.lab_results?.pH);
        case 'labLimeReq': return sample.lab_results?.lime_req_6_5 || '-';
        case 'labPhosphorus': return this.formatSoilTestValue(sample.lab_results?.phosphorus);
        case 'labPIndexGrassland': return sample.lab_results?.phosphorus_index_grassland || '-';
        case 'labPIndexOther': return sample.lab_results?.phosphorus_index_other || '-';
        case 'labPotassium': return this.formatSoilTestValue(sample.lab_results?.potassium, 0);
        case 'labKIndexMineral': return sample.lab_results?.potassium_index_mineral || '-';
        case 'labKIndexPeat': return sample.lab_results?.potassium_index_peat || '-';
        case 'labMagnesium': return this.formatSoilTestValue(sample.lab_results?.magnesium, 0);
        case 'labCalcium': return this.formatSoilTestValue(sample.lab_results?.calcium, 0);
        case 'labCopper': return this.formatSoilTestValue(sample.lab_results?.copper);
        case 'labManganese': return this.formatSoilTestValue(sample.lab_results?.manganese, 1);
        case 'labZinc': return this.formatSoilTestValue(sample.lab_results?.zinc);
        case 'labBoron': return this.formatSoilTestValue(sample.lab_results?.boron, 2);
        case 'labMagnesiumStatus': return this.abbreviateStatus(sample.lab_results?.magnesium_status);
        case 'labCalciumStatus': return this.abbreviateStatus(sample.lab_results?.calcium_status);
        case 'labCopperStatus': return this.abbreviateStatus(sample.lab_results?.copper_status);
        case 'labManganeseStatus': return this.abbreviateStatus(sample.lab_results?.manganese_status);
        case 'labZincStatus': return this.abbreviateStatus(sample.lab_results?.zinc_status);
        case 'labBoronStatus': return this.abbreviateStatus(sample.lab_results?.boron_status);
        case 'labOm': return this.formatSoilTestValue(sample.lab_results?.OM);
        case 'labTestDate': return sample.lab_results?.test_date || '-';
        default: return '';
      }
    },

    csvEscape(value) {
      const text = (value === null || value === undefined) ? '' : String(value);
      return '"' + text.replace(/"/g, '""') + '"';
    },

    exportRecordsCsv() {
      const columns = this.exportColumns;
      const rows = this.sortedSamples;
      if (!columns.length || !rows.length) {
        return;
      }

      const lines = [columns.map(column => this.csvEscape(column.label)).join(',')];
      rows.forEach(sample => {
        lines.push(columns.map(column => this.csvEscape(this.getCellValue(sample, column.key))).join(','));
      });

      // The BOM makes Excel read accented farm and parcel names correctly.
      const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const taskNumber = this.farmteam_task_number || this.task?.id || 'task';
      link.href = url;
      link.download = `task-${taskNumber}-records-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    // Sample ID search handler
    handleSampleIdSearch() {
      // The search filtering happens automatically via computed property
      // No additional action needed since v-model handles the reactive update
    },

    // Farm filter methods
    removeFarmFilter(farmName) {
      const index = this.farmFilter.indexOf(farmName);
      if (index > -1) {
        this.farmFilter.splice(index, 1);
      }
    },

    clearAllFarmFilters() {
      this.farmFilter = [];
      this.showFarmFilterDropdown = false;
    },

    // Column visibility management
    getDefaultColumnVisibility() {
      const defaults = this.getScreenDefaultColumnVisibility();

      // Check if user has saved preferences in localStorage
      const savedPreferences = localStorage.getItem('sample-table-column-visibility');
      if (savedPreferences) {
        try {
          const parsed = JSON.parse(savedPreferences);
          // Ensure the parsed object has all required properties. Columns added since the
          // preferences were saved (e.g. soilType) fall back to their default.
          if (parsed && typeof parsed === 'object' && parsed.actions !== undefined) {
            return { ...defaults, ...parsed };
          }
        } catch (e) {
          console.warn('Invalid column visibility preferences in localStorage, using defaults');
          localStorage.removeItem('sample-table-column-visibility');
        }
      }

      return defaults;
    },

    getScreenDefaultColumnVisibility() {
      // Default visibility based on screen size
      const isMobile = window.innerWidth < 768; // Tailwind's md breakpoint
      const isTablet = window.innerWidth < 1024; // Tailwind's lg breakpoint
      
      if (isMobile) {
        // Mobile: Show only essential columns
        return {
          sampleId: true,
          reference: false,
          soilType: false,
          assigned: true,
          farmName: false,
          parcelNo: false,
          parcelName: false,
          test: true,
          comment: false,
          lpis: false,
          collected: false,
          georef: true,
          result: true,
          // Lab Results Columns
          labPh: false,
          labPhosphorus: false,
          labPotassium: false,
          labMagnesium: false,
          labCalcium: false,
          labCopper: false,
          labManganese: false,
          labZinc: false,
          labBoron: false,
          // Micronutrient Status Columns
          labMagnesiumStatus: false,
          labCalciumStatus: false,
          labCopperStatus: false,
          labManganeseStatus: false,
          labZincStatus: false,
          labBoronStatus: false,
          labOm: false,
          labLimeReq: false,
          labTestDate: false
        };
      } else if (isTablet) {
        // Tablet: Show most columns but hide some less critical ones
        return {
          sampleId: true,
          reference: false,
          soilType: false,
          assigned: true,
          farmName: false,
          parcelNo: false,
          parcelName: false,
          test: true,
          comment: false,
          lpis: false,
          collected: false,
          georef: true,
          result: true,
          // Lab Results Columns  
          labPh: false,
          labPhosphorus: false,
          labPIndexGrassland: false,
          labPIndexOther: false,
          labPotassium: false,
          labKIndexMineral: false,
          labKIndexPeat: false,
          labMagnesium: false,
          labCalcium: false,
          labCopper: false,
          labManganese: false,
          labZinc: false,
          labBoron: false,
          // Micronutrient Status Columns
          labMagnesiumStatus: false,
          labCalciumStatus: false,
          labCopperStatus: false,
          labManganeseStatus: false,
          labZincStatus: false,
          labBoronStatus: false,
          labOm: false,
          labLimeReq: false,
          labTestDate: false
        };
      } else {
        // Desktop: Show most columns by default, hide less commonly used ones
        return {
          sampleId: true,
          reference: false,
          soilType: false,
          assigned: true,
          farmName: false,
          parcelNo: false,
          parcelName: false,
          test: true,
          comment: false,
          lpis: false,
          collected: false,
          georef: true,
          result: true,
          // Lab Results Columns
          labPh: false,
          labPhosphorus: false,
          labPIndexGrassland: false,
          labPIndexOther: false,
          labPotassium: false,
          labKIndexMineral: false,
          labKIndexPeat: false,
          labMagnesium: false,
          labCalcium: false,
          labCopper: false,
          labManganese: false,
          labZinc: false,
          labBoron: false,
          // Micronutrient Status Columns
          labMagnesiumStatus: false,
          labCalciumStatus: false,
          labCopperStatus: false,
          labManganeseStatus: false,
          labZincStatus: false,
          labBoronStatus: false,
          labOm: false,
          labLimeReq: false,
          labTestDate: false
        };
      }
    },
    
    saveColumnVisibility() {
      try {
        if (this.columnVisibility) {
          localStorage.setItem('sample-table-column-visibility', JSON.stringify(this.columnVisibility));
        }
      } catch (e) {
        console.warn('Failed to save column visibility preferences to localStorage:', e);
      }
    },
    
    toggleColumn(column) {
      // Ensure columnVisibility is initialized
      if (!this.columnVisibility) {
        this.columnVisibility = this.getDefaultColumnVisibility();
      }
      // Toggle the column visibility
      this.columnVisibility[column] = !this.columnVisibility[column];
      this.saveColumnVisibility();
    },
    
    resetColumnsToDefault() {
      // Clear localStorage first to ensure we get fresh defaults
      localStorage.removeItem('sample-table-column-visibility');
      this.columnVisibility = this.getDefaultColumnVisibility();
      this.saveColumnVisibility();
    },
    
    showAllColumns() {
      Object.keys(this.columnVisibility).forEach(key => {
        this.columnVisibility[key] = true;
      });
      this.saveColumnVisibility();
    },
    
    hideAllColumns() {
      // Keep essential columns visible - sampleId should remain for identification
      const essentialColumns = ['sampleId'];
      Object.keys(this.columnVisibility).forEach(key => {
        this.columnVisibility[key] = essentialColumns.includes(key);
      });
      this.saveColumnVisibility();
    },

    // Lab Results View Management
    setDataType(type) {
      this.dataType = type;
      // Force values view for OM since it has no index
      if (type === 'om') {
        this.viewType = 'values';
      }
      // Default to interpreted (indices) for major and micro nutrients
      else if (type === 'major' || type === 'micro') {
        this.viewType = 'interpreted';
      }
      this.updateLabResultsView();
    },

    setNutrientType(type) {
      // Legacy compatibility - redirect to setDataType
      this.setDataType(type);
    },

    setViewType(type) {
      this.viewType = type;
      this.updateLabResultsView();
    },

    updateLabResultsView() {
      // Update column visibility based on current data type and view type
      const baseColumns = {
        sampleId: true,
        reference: false,
        soilType: false,
        assigned: false,
        farmName: true,
        parcelNo: false,
        parcelName: true,
        test: false,
        comment: false,
        lpis: false,
        collected: false,
        georef: true,
        result: false
      };

      let labColumns = {
        labPh: false,
        labPhosphorus: false,
        labPIndexGrassland: false,
        labPIndexOther: false,
        labPotassium: false,
        labKIndexMineral: false,
        labKIndexPeat: false,
        labMagnesium: false,
        labCalcium: false,
        labCopper: false,
        labManganese: false,
        labZinc: false,
        labBoron: false,
        labMagnesiumStatus: false,
        labCalciumStatus: false,
        labCopperStatus: false,
        labManganeseStatus: false,
        labZincStatus: false,
        labBoronStatus: false,
        labOm: false,
        labLimeReq: false,
        labTestDate: false
      };

      if (this.dataType === 'overview') {
        // Sample overview - show basic info, hide all lab columns
        baseColumns.parcelNo = true;
        baseColumns.assigned = true;
        baseColumns.reference = true;
        baseColumns.test = true;
        baseColumns.result = true;
      } else if (this.dataType === 'major') {
        // Major nutrients (P, K, Lime)
        labColumns.labPh = true; // Always show pH
        
        if (this.viewType === 'interpreted') {
          // Show indices
          labColumns.labPIndexGrassland = true;
          labColumns.labPIndexOther = true;
          labColumns.labKIndexMineral = true;
          labColumns.labKIndexPeat = true;
          labColumns.labLimeReq = true;
        } else {
          // Show values
          labColumns.labPhosphorus = true;
          labColumns.labPotassium = true;
          labColumns.labLimeReq = true;
        }
      } else if (this.dataType === 'micro') {
        // Micronutrients
        labColumns.labPh = true; // Always show pH
        
        if (this.viewType === 'interpreted') {
          // Show status
          labColumns.labMagnesiumStatus = true;
          labColumns.labCalciumStatus = true;
          labColumns.labCopperStatus = true;
          labColumns.labManganeseStatus = true;
          labColumns.labZincStatus = true;
          labColumns.labBoronStatus = true;
        } else {
          // Show values
          labColumns.labMagnesium = true;
          labColumns.labCalcium = true;
          labColumns.labCopper = true;
          labColumns.labManganese = true;
          labColumns.labZinc = true;
          labColumns.labBoron = true;
        }
      } else if (this.dataType === 'om') {
        // Organic Matter - values only
        labColumns.labOm = true;
      }

      this.columnVisibility = { ...baseColumns, ...labColumns };
      this.saveColumnVisibility();
    },

    // Ensure critical arrays are initialized properly
    ensureArraysInitialized() {
      this.newSamples.forEach(sample => {
        if (!Array.isArray(sample.lab_test)) {
          sample.lab_test = [];
        }
        if (!Array.isArray(sample.georeferences)) {
          sample.georeferences = [];
        }
      });
    },





    // Update all sample georeferences on the map
    updateAllSampleGeoreferencesOnMap() {
      // Clear all temporary multi-sample graphics
      this.$emitter.emit('clearAllMultiSampleGeoreferences');
      
      // Display all samples that have georeferences
      let displayedCount = 0;
      this.newSamples.forEach((sample, index) => {
          if (sample.georeferences && sample.georeferences.length > 0) {
          this.displaySampleGeoreferencesOnMap(sample, index, false); // false = not temporary
          displayedCount++;
        }
      });
    },

    // Display a single sample's georeferences on the map
    displaySampleGeoreferencesOnMap(sample, sampleIndex, isTemporary = false) {
      if (!sample.georeferences || sample.georeferences.length === 0) {
        return;
      }
      
      const sampleId = sample.sample_id || `Sample ${sampleIndex + 1}`;

      
      // Convert [lat, lng] arrays to {lat, lng} objects for map component
      const mapFormatGeorefs = sample.georeferences.map(ref => ({
        lat: ref[0],
        lng: ref[1]
      }));
      
      const sampleColor = this.GEOREFERENCE_COLOR; // Use consistent red color for better visibility
      
      // Add the graphics (caller is responsible for clearing if needed)

      this.$emitter.emit('updateMultiSampleGeoreferences', {
        action: 'update-multi-sample-georeferences',
        sampleId: sampleId,
        sampleIndex: sampleIndex,
        georeferences: mapFormatGeorefs,
        isTemporary: isTemporary,
        sampleData: {
          sample_id: sampleId,
          sample_type: 'Manual Sample',
          depth_from: null,
          depth_to: null,
          georeferences: mapFormatGeorefs
        },
        style: {
          lineStyle: 'dash', // Use consistent dashed line like saved samples
          color: sampleColor,
          width: 1, // Use consistent width like saved samples
          showPoints: true,
          showLabel: true
        }
      });
    },

    startAddingSamples(quantity = 1) {
      if (this.newSamples.length === 0) {
        // Store original column visibility before auto-showing columns
        if (this.columnVisibility) {
          this.originalColumnVisibility = {
            comment: this.columnVisibility.comment,
            lpis: this.columnVisibility.lpis
          };
          
          // Ensure comment and LPIS columns are visible when adding new samples
          this.columnVisibility.comment = true;
          this.columnVisibility.lpis = true;
          this.saveColumnVisibility();
        }
        
        this.newSamples = [];
        for (let i = 0; i < quantity; i++) {
          // For the first sample in batch, try to copy from last existing sample
          let initialLabTest = '';
          if (i === 0 && this.samples && this.samples.length > 0) {
            const lastExistingSample = this.samples[this.samples.length - 1];
            if (lastExistingSample && lastExistingSample.lab_tests) {
              const lastLabTests = Array.isArray(lastExistingSample.lab_tests) 
                ? lastExistingSample.lab_tests 
                : [lastExistingSample.lab_tests];
              
              // If we have lab tests, try to get the code for the first one
              if (lastLabTests.length > 0) {
                const firstTestName = lastLabTests[0];
                initialLabTest = this.getLabTestCodeFromName(firstTestName);
              }
            }
          }
          
          this.newSamples.push({
            sample_id: this.getSuggestedIdForIndex(i),
            comment: '',
            lab_test: [],
            lab_test_string: i === 0 ? initialLabTest : '', // Copy to first sample only
            lpis: '', // LPIS field for all samples
            georeferences: [],
            selectionMethod: null, // Track which method was used: 'manual' or 'parcel'
            parcelInfo: null, // Store parcel information when parcel selection is used
            farmName: null, // Display farm name in UI
            farmId: null, // Store farm ID
            parcelNumber: null, // Display parcel number in UI  
            parcelName: null // Display parcel name in UI
          });
        }
        this.currentSampleIndex = 0;
        this.ensureArraysInitialized();
        
        // Focus the first input after Vue updates the DOM
        this.$nextTick(() => {
          const firstInput = document.querySelector('input[placeholder="Sample ID 1"]');
          if (firstInput) firstInput.focus();
        });
      }
    },

    addAnotherSample(afterIndex = null) {
      console.log('=== addAnotherSample called ===', afterIndex);
      
      // Always add to the end of the list to avoid confusion
      const insertIndex = this.newSamples.length;
      
      // Generate suggested ID based on the highest existing ID + 1
      let suggestedId = '';
      
      // Find the highest numeric ID to ensure no duplicates
      const existingIds = [
        ...this.samples.map(s => s.id || s.sample_id),
        ...this.newSamples.map(s => s.sample_id)
      ].filter(id => id && id.toString().trim());
      
      if (existingIds.length > 0) {
        // Extract numbers from all IDs and find the highest
        const numbers = existingIds.map(id => {
          const match = id.toString().match(/(\d+)(?!.*\d)/); // Find last number in string
          return match ? parseInt(match[1]) : 0;
        }).filter(num => !isNaN(num));
        
        const maxNum = Math.max(...numbers, 0);
        
        // Check if we should maintain a prefix pattern from the last sample
        const lastSample = this.newSamples[this.newSamples.length - 1];
        if (lastSample && lastSample.sample_id.trim()) {
          const lastId = lastSample.sample_id.toString();
          const match = lastId.match(/^(.*?)(\d+)$/);
          if (match) {
            const prefix = match[1];
            const nextNumber = maxNum + 1;
            
            // Maintain zero-padding if it existed
            const originalNumberStr = match[2];
            if (originalNumberStr.startsWith('0') && originalNumberStr.length > 1) {
              const paddedNumber = nextNumber.toString().padStart(originalNumberStr.length, '0');
              suggestedId = prefix + paddedNumber;
            } else {
              suggestedId = prefix + nextNumber;
            }
          } else {
            suggestedId = (maxNum + 1).toString();
          }
        } else {
          suggestedId = (maxNum + 1).toString();
        }
      } else {
        suggestedId = '1';
      }
      
      // Get lab test from previous row if available
      const previousLabTest = this.newSamples.length > 0 
        ? this.newSamples[this.newSamples.length - 1].lab_test_string 
        : '';
      
      // Create new sample
      const newSample = {
        sample_id: suggestedId,
        comment: '',
        lab_test: [],
        lab_test_string: previousLabTest, // Copy from previous row
        lpis: '', // LPIS field for all samples
        georeferences: [],
        selectionMethod: null, // Track which method was used: 'manual' or 'parcel'
        parcelInfo: null, // Store parcel information when parcel selection is used
        farmName: null, // Display farm name in UI
        farmId: null, // Store farm ID
        parcelNumber: null, // Display parcel number in UI  
        parcelName: null // Display parcel name in UI
      };
      
      // Add the new sample to the end
      this.newSamples.push(newSample);
      
      // Update array initialization
      this.ensureArraysInitialized();
      
      // Focus the new sample's input after Vue updates the DOM
      this.$nextTick(() => {
        setTimeout(() => {
          const inputs = document.querySelectorAll('input[placeholder^="Suggested:"]');
          const newInput = inputs[inputs.length - 1];
          
          if (newInput) {
            newInput.focus();
            
            // Find the scrollable container that contains the table
            const tableRow = newInput.closest('tr');
            if (tableRow) {
              // Walk up the DOM to find the first scrollable parent
              let scrollableParent = newInput;
              while (scrollableParent && scrollableParent !== document.body) {
                const computedStyle = window.getComputedStyle(scrollableParent);
                const hasVerticalScroll = scrollableParent.scrollHeight > scrollableParent.clientHeight;
                const overflowY = computedStyle.overflowY;
                
                if (hasVerticalScroll && (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')) {
                  // Found the scrollable container
                  scrollableParent.scrollTo({
                    top: tableRow.offsetTop - scrollableParent.clientHeight / 2 + tableRow.offsetHeight / 2,
                    behavior: 'smooth'
                  });
                  break;
                }
                scrollableParent = scrollableParent.parentElement;
              }
              
              // Fallback: use scrollIntoView if no specific container found
              if (scrollableParent === document.body) {
                tableRow.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
              }
            }
          }
        }, 100); // Reduced delay since we're now targeting the right container
      });
      
      if (this.$toast) {
        this.$toast.success(`Added new sample with ID: ${suggestedId}`);
      }
    },

    cancelNewSample() {
      // Restore original column visibility
      this.restoreOriginalColumnVisibility();
      
      this.newSamples = [];
      this.currentSampleIndex = 0;
      this.showGeoreferenceError = false;
      this.isSelectingGeoreferences = false;
      this.parcelSelectionMode = false; // Reset parcel selection mode
      
      // Clear all multi-sample georeferences from the map and stop any active selection
      this.$emitter.emit('clearAllMultiSampleGeoreferences');
      this.$emitter.emit('stopGeoreferenceSelection');
    },

    removeSampleRow(index) {
      if (this.newSamples.length === 1) {
        // If only one row, cancel all
        this.cancelNewSample();
      } else {
        // Remove specific row
        this.newSamples.splice(index, 1);
        // Adjust currentSampleIndex if needed
        if (this.currentSampleIndex >= this.newSamples.length) {
          this.currentSampleIndex = Math.max(0, this.newSamples.length - 1);
        }
      }
    },

    // Refresh ID suggestions for empty sample ID fields
    refreshIdSuggestions() {
      this.newSamples.forEach((sample, index) => {
        if (!sample.sample_id.trim()) {
          sample.sample_id = this.getSuggestedIdForIndex(index);
        }
      });
    },
    
    // Called when user manually changes a sample ID - updates subsequent suggestions
    onSampleIdChange(index) {
      // Update suggestions for all subsequent empty fields
      for (let i = index + 1; i < this.newSamples.length; i++) {
        if (!this.newSamples[i].sample_id.trim()) {
          this.newSamples[i].sample_id = this.getSuggestedIdForIndex(i);
        }
      }
    },

    copyLabTestToSubsequentRows(fromIndex) {
      const selectedLabTest = this.newSamples[fromIndex].lab_test_string;
      if (selectedLabTest) {
        // Copy to all subsequent rows that don't have a lab test selected
        for (let i = fromIndex + 1; i < this.newSamples.length; i++) {
          if (!this.newSamples[i].lab_test_string) {
            this.newSamples[i].lab_test_string = selectedLabTest;
          }
        }
      }
    },





    resetNewSample() {
      this.newSample = { 
        sample_id: '', 
        comment: '', 
        lab_test: [], 
        lab_test_string: '', 
        lpis: '', // LPIS field for all samples
        georeferences: [],
        selectionMethod: null, // Track which method was used: 'manual' or 'parcel'
        parcelInfo: null, // Store parcel information when parcel selection is used
        farmName: null, // Display farm name in UI
        farmId: null, // Store farm ID
        parcelNumber: null, // Display parcel number in UI  
        parcelName: null // Display parcel name in UI
      };
      this.showGeoreferenceError = false;
      this.isSelectingGeoreferences = false;
      
      // Clean up map state
      if (this.$root.showMapOnMobile) {
        this.$root.showMapOnMobile = false;
      }
      
      // Cancel any active map interactions
      this.$emitter.emit('cancelFarmInteraction');
    },

    isDuplicateSampleId(sampleId, currentIndex = null) {
      if (!sampleId || !sampleId.trim()) return false;
      
      const trimmedId = sampleId.trim().toLowerCase();
      
      // Check against existing samples
      const existsInSamples = this.samples.some(sample => 
        sample.id && sample.id.toLowerCase() === trimmedId
      );
      if (existsInSamples) return true;
      
      // Check against other new samples (excluding current one)
      const existsInNewSamples = this.newSamples.some((sample, index) => 
        index !== currentIndex && sample.sample_id.trim().toLowerCase() === trimmedId
      );
      
      return existsInNewSamples;
    },

    handleNewSampleKeyDown(event, index, field) {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (this.canSaveNewSampleAtIndex(index)) {
          this.saveNewSample(index);
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.removeSampleRow(index);
      }
    },

    startGeoreferenceSelectionForNew() {
      this.isSelectingGeoreferences = true;
      this.$root.showMapOnMobile = true;
      
      if (this.$toast) {
        this.$toast.info('Click points on the map to add georeferences.');
      }

      // Enable point selection mode on map
      this.$emitter.emit('addFarmPoint', {
        action: 'georeference-selection',
        type: 'georeference-selection',
        multiPoint: true
      });
    },

    locateExistingSample(sample) {
      
      
      if (!sample || (this.getGeoreferenceCount(sample) === 0 && !this.isSMAPSample(sample))) {
        if (this.$toast) {
          this.$toast.error('No georeference data or GPS path available for this sample.');
        }
        return;
      }
      
      let georeferences = [];
      
      let isSMAPSample = false;
      
      // Handle manual georeferences (from task records)
      if (sample.manual_georeferences && Array.isArray(sample.manual_georeferences) && sample.manual_georeferences.length > 0) {
        georeferences = sample.manual_georeferences.map(ref => {
          // Handle different formats
          if (Array.isArray(ref) && ref.length === 2) {
            return { lat: ref[0], lng: ref[1] };
          }
          return { lat: ref.lat || ref[0], lng: ref.lng || ref.lon || ref[1] };
        });
      }
      // Handle SMAP sample path georeferences with markers (newer format)
      else if (sample.markers && Array.isArray(sample.markers) && sample.markers.length > 0 && sample.paths_lat_lon) {
        isSMAPSample = true;
        const paths = sample.paths_lat_lon[0] || [];
        sample.markers.forEach(markerIndex => {
          if (markerIndex >= 0 && markerIndex < paths.length) {
            const [lng, lat] = paths[markerIndex];
            if (lng && lat) {
              georeferences.push({ lat, lng });
            }
          }
        });
      }
      // Handle older SMAP samples without markers (backwards compatibility)
      else if (sample.paths_lat_lon && Array.isArray(sample.paths_lat_lon) && sample.paths_lat_lon.length > 0) {
        isSMAPSample = true;
        const paths = sample.paths_lat_lon[0] || [];
        
        // For legal compliance, use mid-point instead of all coordinates for samples after Sept 15, 2025
        const sampleDate = sample.sample_date_end ? new Date(sample.sample_date_end) : null;
        const georeferenceRequirementDate = new Date('2025-09-15');
        
        if (sampleDate && sampleDate >= georeferenceRequirementDate && paths.length > 0) {
          // Use the mid-point of the path as the georeference for legal compliance
          const midIndex = Math.floor(paths.length / 2);
          const coord = paths[midIndex];
          if (Array.isArray(coord) && coord.length >= 2) {
            const [lng, lat] = coord;
            if (typeof lng === 'number' && typeof lat === 'number') {
              georeferences.push({ lat, lng });
            }
          }
        }
        // For older samples (before Sept 15, 2025), still allow locating the full GPS path
        // even though there's no georeference requirement
      }
      
      // Use different events based on sample type
      if (isSMAPSample) {
        // For SMAP samples, zoom to the sample path and highlight it
        // If no specific georeferences, the map component will show the full GPS path
        this.$emitter.emit('zoomToExistingSamplePath', {
          action: 'zoom-to-existing-path',
          sampleId: sample.sample_id || sample.id,
          georeferences: georeferences.length > 0 ? georeferences : null,
          pathData: sample.paths_lat_lon // Include full path data for samples without specific georeferences
        });
      }
      else if (georeferences.length === 0) {
        if (this.$toast) {
          this.$toast.error('No valid georeference coordinates found for this sample.');
        }
        return;
      } else {
        // For manual samples, display the sample on the map and zoom to it
        this.$emitter.emit('updateSampleGeoreferences', {
          action: 'locate-sample',
          sampleId: sample.sample_id || sample.id,
          sampleIndex: -1, // Use -1 to indicate this is an existing sample
          georeferences: georeferences,
          zoom: true, // Enable zoom for this operation
          sampleData: {
            sample_id: sample.sample_id || sample.id,
            sample_type: 'Manual Sample',
            depth_from: null,
            depth_to: null,
            georeferences: georeferences
          },
          style: {
            color: this.GEOREFERENCE_COLOR
          }
        });
      }
      

    },
    
    locateNewSample(index) {
      const sample = this.newSamples[index];
      if (!sample || !sample.georeferences || sample.georeferences.length === 0) {
        if (this.$toast) {
          this.$toast.error('No georeference data available for this sample.');
        }
        return;
      }
      
      // Convert georeferences to the format expected by the map
      const georeferences = sample.georeferences.map(ref => ({
        lat: ref[0],
        lng: ref[1]
      }));
      
      // Display the sample on the map and zoom to it
      this.$emitter.emit('updateSampleGeoreferences', {
        action: 'locate-sample',
        sampleId: sample.sample_id || `Sample ${index + 1}`,
        sampleIndex: index,
        georeferences: georeferences,
        zoom: true, // Enable zoom for this operation
        sampleData: {
          sample_id: sample.sample_id || `Sample ${index + 1}`,
          sample_type: 'New Sample',
          depth_from: null,
          depth_to: null,
          georeferences: georeferences
        },
        style: {
          color: this.GEOREFERENCE_COLOR
        }
      });
      

    },
    
    // Get suggested sample ID for a specific index in the new samples array
    getSuggestedIdForIndex(index) {
      // First, check if user has established a pattern in current session
      const userEnteredIds = this.newSamples
        .slice(0, index)
        .map(s => s.sample_id)
        .filter(id => id.trim());
      
      if (userEnteredIds.length > 0) {
        // Use the most recent user-entered ID as the pattern base
        const lastUserEntry = userEnteredIds[userEnteredIds.length - 1];
        return this.getNextIdFromPattern(lastUserEntry, userEnteredIds.length);
      }
      
      // Fallback to existing samples pattern
      const existingIds = this.samples.map(s => s.id);
      
      if (existingIds.length === 0) {
        return (index + 1).toString();
      }
      
      // Find the highest existing ID to continue the pattern
      const numericIds = existingIds
        .map(id => {
          const match = id.toString().match(/(\d+)$/);
          return match ? parseInt(match[1]) : null;
        })
        .filter(num => num !== null);
      
      if (numericIds.length > 0) {
        const maxNum = Math.max(...numericIds);
        const baseIncrement = maxNum + 1 + index;
        
        // Check if we should maintain a prefix pattern
        const highestId = existingIds.find(id => id.toString().endsWith(maxNum.toString()));
        if (highestId && !/^\d+$/.test(highestId.toString())) {
          const prefix = highestId.toString().replace(/\d+$/, '');
          return prefix + baseIncrement;
        }
        
        return baseIncrement.toString();
      }
      
      return (existingIds.length + 1 + index).toString();
    },
    
    // Extract pattern from user's manual entry and generate next ID
    getNextIdFromPattern(lastId, incrementBy = 1) {
      const match = lastId.toString().match(/^(.*)?(\d+)$/);
      
      if (match) {
        const prefix = match[1] || '';
        const number = parseInt(match[2]);
        const nextNumber = number + incrementBy;
        
        // Maintain zero-padding if it existed
        const originalNumberStr = match[2];
        if (originalNumberStr.startsWith('0') && originalNumberStr.length > 1) {
          const paddedNumber = nextNumber.toString().padStart(originalNumberStr.length, '0');
          return prefix + paddedNumber;
        }
        
        return prefix + nextNumber;
      }
      
      // If no pattern found, append number
      return lastId + '1';
    },

    // Method to check if a specific sample can be saved (indexed version)
    canSaveNewSampleAtIndex(index) {
      const sample = this.newSamples[index];
      if (!sample) {
        
        return false;
      }
      
      // Check that georeferences array exists and has at least one point with valid coordinates
      const hasValidGeoreferences = sample.georeferences && 
                                   Array.isArray(sample.georeferences) && 
                                   sample.georeferences.length > 0 &&
                                   sample.georeferences.every(geo => Array.isArray(geo) && geo.length === 2 && typeof geo[0] === 'number' && typeof geo[1] === 'number');
      
      // Check that this sample is not currently in georeference selection mode
      const notSelectingGeoreferences = !this.isSelectingGeoreferences || this.currentSampleIndex !== index;
      
      const checks = {
        sample_id: !!sample.sample_id.trim(),
        lab_test_string: !!sample.lab_test_string,
        hasValidGeoreferences: hasValidGeoreferences,
        notSelectingGeoreferences: notSelectingGeoreferences,
        notDuplicate: !this.isDuplicateSampleId(sample.sample_id, index)
      };
      
      const canSave = sample.sample_id.trim() && 
                     sample.lab_test_string && 
                     hasValidGeoreferences &&
                     notSelectingGeoreferences &&
                     !this.isDuplicateSampleId(sample.sample_id, index);
      

      
      return canSave;
    },

    // Save all valid samples
    async saveAllSamples() {

      
      const validSamples = this.newSamples.filter((sample, index) => 
        this.canSaveNewSampleAtIndex(index)
      );
      

      
      if (validSamples.length === 0) {
        
        return;
      }
      
      this.isCreating = true;
      
      try {
        // Process samples sequentially to preserve creation order
        // This ensures samples are saved to database in the correct order for FBA payload
        const results = [];
        
        for (const sample of validSamples) {
          const labTestArray = sample.lab_test_string ? [sample.lab_test_string] : [];
          
          const payload = {
            sample_id: sample.sample_id,
            comment: sample.comment || null,
            lab_tests: labTestArray,
            manual_georeferences: sample.georeferences || [],
            lpis: sample.lpis || null, // Include LPIS field for all samples
            parcel_info: sample.parcelInfo || null // Include parcel information for database
          };
          
          try {
            const response = await axios.post(route('app.task.create-manual-sample', this.task.id), payload);
            results.push(response);
          } catch (error) {
            console.error(`Server error for sample ${sample.sample_id}:`, error.response?.data);
            throw error;
          }
        }

        
        // Success - refresh and reset
        // Restore original column visibility before clearing samples
        this.restoreOriginalColumnVisibility();

        this.$inertia.reload({ 
          only: ['samples', 'tests', 'labUpdateChanges', 'labUpdateNeeded'],
          onSuccess: () => {
            // Show lab order banner after successful sample creation
            this.showLabOrderBannerAfterSave = true;
            this.labOrderBannerDismissed = false;
          }
        });
        this.cancelNewSample(); // This will clear all multi-sample graphics
        
        if (this.$toast) {
          const count = validSamples.length;
          this.$toast.success(`${count} sample${count > 1 ? 's' : ''} created successfully!`);
        }
      } catch (error) {
        console.error('Bulk sample creation error:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          statusText: error.response?.statusText
        });
        if (this.$toast) {
          const message = error.response?.data?.message || 'Failed to create samples. Please try again.';
          this.$toast.error(message);
          
          // Show validation errors if available
          if (error.response?.data?.errors) {
            console.error('Validation errors:', error.response.data.errors);
            Object.entries(error.response.data.errors).forEach(([field, messages]) => {
              console.error(`Field ${field}:`, messages);
            });
          }
        }
      } finally {
        this.isCreating = false;

      }
    },

    // Save and add another sample


    async saveNewSample(index = null) {
      // Handle both indexed and legacy calls
      if (index === null) {
        // Legacy support - save the first valid sample
        const firstValidIndex = this.newSamples.findIndex((sample, i) => 
          this.canSaveNewSampleAtIndex(i)
        );
        if (firstValidIndex >= 0) {
          return this.saveNewSample(firstValidIndex);
        }
        return;
      }
      
      if (!this.canSaveNewSampleAtIndex(index)) return;
      
      const sample = this.newSamples[index];
      this.isCreating = true;
      
      try {
        // Convert lab_test_string to array format expected by backend
        const labTestArray = sample.lab_test_string ? [sample.lab_test_string] : [];
        

        
        const response = await axios.post(route('app.task.create-manual-sample', this.task.id), {
          sample_id: sample.sample_id,
          comment: sample.comment || null,
          lab_tests: labTestArray,
          manual_georeferences: sample.georeferences || [],
          lpis: sample.lpis || null
        });

        // Success - refresh samples and remove this sample from newSamples
        this.$inertia.reload({ 
          only: ['samples', 'tests', 'labUpdateChanges', 'labUpdateNeeded'],
          onSuccess: () => {
            // Show lab order banner after successful sample creation
            this.showLabOrderBannerAfterSave = true;
            this.labOrderBannerDismissed = false;
          }
        });
        this.removeSampleRow(index);
        
        if (this.$toast) {
          this.$toast.success('Sample created successfully!');
        }
      } catch (error) {
        console.error('Sample creation error:', error);
        if (this.$toast) {
          const message = error.response?.data?.message || 'Failed to create sample. Please try again.';
          this.$toast.error(message);
        }
      } finally {
        this.isCreating = false;
      }
    },



    startGeoreferenceSelection() {
      // Enter multi-point selection mode
      this.isSelectingGeoreferences = true;
      this.showMapInteractionMode = true;
      this.$root.showMapOnMobile = true;
      
      if (this.$toast) {
        this.$toast.info(`GEOREFERENCE MODE
Click points on the map to add locations.
Click "Finish" when complete.`);
      }

      // Enable point selection mode on map
      this.$emitter.emit('addFarmPoint', {
        action: 'georeference-selection',
        type: 'georeference-selection',
        multiPoint: true
      });
    },

    finishGeoreferenceSelection() {
      // Exit selection mode
      this.isSelectingGeoreferences = false;
      this.showMapInteractionMode = false;
      this.$root.showMapOnMobile = false;
      this.parcelSelectionMode = false; // Completely disable parcel selection mode

      // Disable selection mode on map using the correct event
      this.$emitter.emit('stopGeoreferenceSelection');

      // Update map to show ALL samples with their georeferences (multi-sample view)
      this.updateAllSampleGeoreferencesOnMap();

      const currentSample = this.newSamples[this.currentSampleIndex];
      if (this.$toast) {
        if (currentSample) {
          this.$toast.success(`Georeference selection complete! ${currentSample.georeferences.length} point(s) saved for ${currentSample.sample_id || `Sample ${this.currentSampleIndex + 1}`}.`);
        }
      }
    },

    clearLabTest(index) {
      this.newSamples[index].lab_test_string = '';
    },

    // Handle lab selection change in modal
    onLabSelectionChange() {
      // This will trigger the computed properties to update
      // modalLabTests and transformedLabTests will automatically refresh
    },

    displayExistingSampleGeoreferences() {

      
      // Display georeferences for existing samples that have manual_georeferences
      if (this.samples && Array.isArray(this.samples)) {
        let displayedCount = 0;
        
        this.samples.forEach((sample, index) => {
          if (sample.manual_georeferences && Array.isArray(sample.manual_georeferences) && sample.manual_georeferences.length > 0) {
            // Convert manual_georeferences to the format expected by the map
            const georeferences = sample.manual_georeferences.map(ref => {
              // New format: [lat, lng] arrays
              if (Array.isArray(ref) && ref.length === 2) {
                return { lat: ref[0], lng: ref[1] };
              }
              // Fallback (shouldn't be needed after cleanup)
              return { lat: ref.lat || ref[0], lng: ref.lng || ref.lon || ref[1] };
            });

            //console.log(`Displaying georeferences for sample ${sample.sample_id}:`, georeferences.length, 'points');

            // Emit event to display this sample's georeferences on the map using the existing single-sample system
            this.$emitter.emit('updateSampleGeoreferences', {
              action: 'update-sample-georeferences',
              sampleId: sample.sample_id,
              sampleIndex: index,
              georeferences: georeferences,
              sampleData: {
                sample_id: sample.sample_id,
                sample_type: 'Manual Sample',
                depth_from: null,
                depth_to: null,
                georeferences: georeferences
              },
              style: {
                lineStyle: 'dashed',
                color: this.GEOREFERENCE_COLOR, // red for better visibility
                width: 1, // match normal sample path thickness
                showPoints: true,
                showLabel: true
              }
            });
            
            displayedCount++;
          }
        });
        
        //console.log(`Displayed georeferences for ${displayedCount} samples`);
      } else {
        //console.log('No samples available or samples is not an array');
      }
    },

    cancelGeoreferenceSelection() {
      // Clear any georeferences that were added during this selection session
      const currentSample = this.newSamples[this.currentSampleIndex];
      if (currentSample) {
        const sampleId = currentSample.sample_id || `Sample ${this.currentSampleIndex + 1}`;
        
        console.log('❌ CANCEL GEOREFERENCE SELECTION - Starting for:', sampleId);
        console.log('❌ Georeferences before cancel:', currentSample.georeferences?.length || 0);
        
        console.log('❌ Emitting clearAllMultiSampleGeoreferences event');
        // Clear all multi-sample graphics to remove ALL graphics including labels and points
        this.$emitter.emit('clearAllMultiSampleGeoreferences');
        
        currentSample.georeferences = [];
        console.log('❌ CANCEL GEOREFERENCE SELECTION - Completed');
      }
      
      // Exit selection mode without saving
      this.isSelectingGeoreferences = false;
      this.showMapInteractionMode = false;
      this.$root.showMapOnMobile = false;
      this.parcelSelectionMode = false; // Completely disable parcel selection mode

      // Disable point selection mode on map and clear any temporary markers
      this.$emitter.emit('stopGeoreferenceSelection');
      
      // Add a small delay to ensure map interaction is fully disabled before proceeding
      setTimeout(() => {
        // Since we've cleared all graphics and cleared the current sample's georeferences,
        // we need to re-display any OTHER samples that still have georeferences
        const otherSamplesWithGeoreferences = this.newSamples.filter((sample, index) => 
          index !== this.currentSampleIndex && sample.georeferences && sample.georeferences.length > 0
        );
        
        if (otherSamplesWithGeoreferences.length > 0) {
          this.updateAllSampleGeoreferencesOnMap();
        } else {
          // Double-check that all graphics are still cleared
          setTimeout(() => {
            this.$emitter.emit('clearAllMultiSampleGeoreferences');
          }, 100);
        }
      }, 50);

      if (this.$toast) {
        this.$toast.info('Georeference selection cancelled. Points cleared for this sample.');
      }
    },

    clearSampleGeoreferences(sampleIndex) {
      const sample = this.newSamples[sampleIndex];
      if (sample && sample.georeferences.length > 0) {
        if (this.$swal) {
          this.$swal.fire({
            title: 'Clear all georeferences?',
            text: `This will remove all ${sample.georeferences.length} point(s) for this sample.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, clear all',
            cancelButtonText: 'Cancel'
          }).then((result) => {
            if (result.isConfirmed) {
              console.log('🧹 CLEAR SAMPLE GEOREFERENCES (SweetAlert) - Starting cleanup for:', sample.sample_id || `Sample ${sampleIndex + 1}`);
              console.log('🧹 Georeferences before clear:', sample.georeferences?.length || 0);
              
              sample.georeferences = [];
              sample.selectionMethod = null; // Reset selection method
              sample.parcelInfo = null; // Clear parcel information
              // Clear farm and parcel display information
              sample.farmName = null;
              sample.farmId = null;
              sample.parcelNumber = null;
              sample.parcelName = null;
              
              console.log('🧹 Emitting clearAllMultiSampleGeoreferences event');
              // Clear all multi-sample graphics first
              this.$emitter.emit('clearAllMultiSampleGeoreferences');
              
              console.log('🧹 Calling updateAllSampleGeoreferencesOnMap');
              // Update all samples on map (this will automatically exclude the cleared sample)
              this.updateAllSampleGeoreferencesOnMap();
              
              console.log('🧹 CLEAR SAMPLE GEOREFERENCES (SweetAlert) - Completed');
              
              if (this.$toast) {
                this.$toast.success('All georeferences cleared for this sample.');
              }
            }
          });
        } else {
          // Fallback if SweetAlert is not available
          if (confirm(`Clear all ${sample.georeferences.length} georeference point(s) for this sample?`)) {
            console.log('🧹 CLEAR SAMPLE GEOREFERENCES (Fallback) - Starting cleanup for:', sample.sample_id || `Sample ${sampleIndex + 1}`);
            console.log('🧹 Georeferences before clear:', sample.georeferences?.length || 0);
            
            sample.georeferences = [];
            sample.selectionMethod = null; // Reset selection method
            sample.parcelInfo = null; // Clear parcel information
            // Clear farm and parcel display information
            sample.farmName = null;
            sample.farmId = null;
            sample.parcelNumber = null;
            sample.parcelName = null;
            
            console.log('🧹 Emitting clearAllMultiSampleGeoreferences event');
            // Clear all multi-sample graphics first
            this.$emitter.emit('clearAllMultiSampleGeoreferences');
            
            console.log('🧹 Calling updateAllSampleGeoreferencesOnMap');
            // Update all samples on map (this will automatically exclude the cleared sample)
            this.updateAllSampleGeoreferencesOnMap();
            
            console.log('🧹 CLEAR SAMPLE GEOREFERENCES (Fallback) - Completed');
            
            if (this.$toast) {
              this.$toast.success('All georeferences cleared for this sample.');
            }
          }
        }
      }
    },

    clearAllGeoreferences() {
      if (this.$swal) {
        this.$swal.fire({
          title: 'Clear all georeferences?',
          text: 'This will remove all selected points from the map.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, clear all',
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            const currentSample = this.newSamples[this.currentSampleIndex];
            if (currentSample) {
              currentSample.georeferences = [];
            }
            this.$emitter.emit('clearGeoreferences', {
              action: 'clear-georeferences'
            });
            if (this.$toast) {
              this.$toast.success('All georeferences cleared.');
            }
          }
        });
      } else {
        // Fallback if SweetAlert is not available
        if (confirm('Clear all georeferences? This will remove all selected points.')) {
          const currentSample = this.newSamples[this.currentSampleIndex];
          if (currentSample) {
            currentSample.georeferences = [];
          }
          this.$emitter.emit('clearGeoreferences', {
            action: 'clear-georeferences'
          });
        }
      }
    },

    removeGeoreference(index) {
      const currentSample = this.newSamples[this.currentSampleIndex];
      if (currentSample && currentSample.georeferences) {
        currentSample.georeferences.splice(index, 1);
      }
      
      // Update the map to reflect the removal
      this.$emitter.emit('updateGeoreferences', {
        action: 'update-georeferences',
        georeferences: this.newSamples[this.currentSampleIndex]?.georeferences || []
      });

      if (this.$toast) {
        this.$toast.info('Georeference point removed.');
      }
    },

    selectGeoreferencesForNewSample(index = 0) {
      console.log('Setting up georeference selection for sample index:', index);
      
      this.currentSampleIndex = index;
      this.isSelectingGeoreferences = true;
      
      // Ensure the sample has georeferences array initialized
      if (!this.newSamples[index].georeferences) {
        this.newSamples[index].georeferences = [];
      }
      
      // Mark this sample as using manual selection method
      this.newSamples[index].selectionMethod = 'manual';
      
      // Make sure the map is visible and in georeference mode
      if (this.$root.showMapOnMobile !== undefined) {
        this.$root.showMapOnMobile = true;
      }
      
      // Use the correct event that the map actually listens for
      this.$emitter.emit('addFarmPoint', {
        action: 'georeference-selection',
        type: 'georeference-selection',
        multiPoint: true,
        sampleIndex: index
      });
      

      if (this.$toast) {
        this.$toast.info(`GEOREFERENCE MODE\nClick points on the map to add locations for Sample ${index + 1}.\nClick "Finish" when complete.`);
      }
    },

    selectParcelGeoreference(index = 0) {

      
      this.currentSampleIndex = index;
      this.isSelectingGeoreferences = true;
      this.parcelSelectionMode = 'parcel';
      
      // Ensure the sample has georeferences array initialized
      if (!this.newSamples[index].georeferences) {
        this.newSamples[index].georeferences = [];
      }
      
      // Mark this sample as using parcel selection method
      this.newSamples[index].selectionMethod = 'parcel';
      
      // Make sure the map is visible and in parcel selection mode
      if (this.$root.showMapOnMobile !== undefined) {
        this.$root.showMapOnMobile = true;
      }
      
      // Activate parcel selection mode
      const usedIds = this.usedParcelIds;

      
      this.$emitter.emit('addFarmPoint', {
        action: 'parcel-georeference-selection',
        type: 'parcel-georeference-selection',
        multiPoint: false, // Single parcel selection only
        sampleIndex: index,
        usedParcelIds: usedIds // Pass list of unavailable parcels
      });
      
      if (this.$toast) {
        this.$toast.info(`PARCEL SELECTION MODE\nClick on a parcel boundary to use its center point as the georeference for Sample ${index + 1}.`);
      }
    },

    handleGeoreferenceMapClick(payload) {
      // Exit early if we're not in selection mode
      if (!this.isSelectingGeoreferences) {
        return;
      }
      
      // Handle both manual and parcel georeference selection
      if (payload.action !== "georeference-selection" && payload.action !== "parcel-georeference-selection") {
        return;
      }

      // Keep map visible and active during multi-point selection mode
      // Only hide map if we're not in multi-point selection mode
      if (!this.isSelectingGeoreferences) {
        this.$root.showMapOnMobile = false;
        this.showMapInteractionMode = false;
      } else {
        // In multi-point mode, keep map visible and active

      }

        // Extract coordinates from the payload
        if (payload.identifier && payload.identifier.length >= 2) {
          const coords = payload.identifier;
          const lng = coords[0]; // longitude
          const lat = coords[1]; // latitude



          // Add the new georeference point to current sample
          const currentSample = this.newSamples[this.currentSampleIndex];
          if (currentSample && Array.isArray(currentSample.georeferences)) {
            // For parcel selection, replace any existing georeferences (single selection only)
            if (payload.action === "parcel-georeference-selection") {
              currentSample.georeferences = [[lat, lng]];
              // Store parcel information for database saving
              currentSample.parcelInfo = payload.parcelInfo || null;
              
              // Update Farm and Parcel information in the UI immediately
              if (payload.parcelInfo) {
                this.updateSampleFarmAndParcelInfo(currentSample, payload.parcelInfo);
              }
            } else {
              // For manual selection, add to existing georeferences (multi-point)
              currentSample.georeferences.push([lat, lng]);
              // Clear parcel info for manual selections
              currentSample.parcelInfo = null;
              // Clear farm and parcel display info for manual selections
              currentSample.farmName = null;
              currentSample.parcelNumber = null;
              currentSample.parcelName = null;
            }
          }        this.showGeoreferenceError = false;



        // Clear all existing graphics first to prevent duplicates
        this.$emitter.emit('clearAllMultiSampleGeoreferences');
        
        // Update the map to show all samples with the currently active one as temporary
        this.newSamples.forEach((sample, index) => {
          if (sample.georeferences && sample.georeferences.length > 0) {
            const isCurrentSample = index === this.currentSampleIndex;
            this.displaySampleGeoreferencesOnMap(sample, index, isCurrentSample); // Current sample is temporary
          }
        });
      } else {
        console.log('Invalid coordinates in payload:', payload);
      }
    },

    // Update sample with farm and parcel information from selected parcel
    updateSampleFarmAndParcelInfo(sample, parcelInfo) {
      if (!parcelInfo) return;
      
      // Find the farm that contains this parcel
      const farmInfo = this.findFarmByParcelInfo(parcelInfo);
      
      if (farmInfo) {
        // Update sample with farm information
        sample.farmName = farmInfo.farm.name || 'Unknown Farm';
        sample.farmId = farmInfo.farm.item_id;
        
        // Update sample with parcel information
        const parcel = farmInfo.parcel;
        if (parcel) {
          // Construct parcel number with suffix
          const fieldNo = parcel.field_no || '';
          const suffix = parcel.field_no_suffix || '';
          sample.parcelNumber = fieldNo + suffix;
          sample.parcelName = parcel.name || '';
        }
        
        console.log('Updated sample with farm/parcel info:', {
          sampleId: sample.sample_id,
          farmName: sample.farmName,
          parcelNumber: sample.parcelNumber,
          parcelName: sample.parcelName
        });
      }
    },

    // Find farm and parcel information from parcel hashid
    findFarmByParcelInfo(parcelInfo) {
      if (!parcelInfo?.hashid || !this.$root.farmer?.farms) {
        return null;
      }
      
      const parcelHashid = parcelInfo.hashid;
      
      // Search through all farms and their parcels
      for (const farm of this.$root.farmer.farms) {
        if (farm.parcels && Array.isArray(farm.parcels)) {
          for (const parcel of farm.parcels) {
            if (parcel.hashid === parcelHashid) {
              return { farm, parcel };
            }
          }
        }
      }
      
      return null;
    },

    handleParcelUnavailable(payload) {
      console.log('Parcel unavailable:', payload);
      
      if (this.$toast) {
        const parcelName = payload.parcelInfo?.name || 'Selected parcel';
        this.$toast.error(`Parcel "${parcelName}" is already assigned to another sample and cannot be selected.`);
      }
    },

    goTo(url) {
      window.location.href = url;
    },

    // Lab order banner methods
    async sendToLab() {
      if (this.isProcessingLabOrder) return;
      
      this.isProcessingLabOrder = true;
      
      try {
        // Find the first test that hasn't been sent to lab, or use the first test if all have been sent
        const unsentTest = this.tests.find(test => !test.sent_to_lab);
        const targetTest = unsentTest || this.tests[0];
        
        if (!targetTest) {
          if (this.$toast) {
            this.$toast.error('No test found to send to lab.');
          }
          return;
        }

        // Use Inertia.js for SPA navigation instead of full page reload
        const currentTab = this.currentTab || 'records';
        this.$inertia.get(route('app.task.send-to-lab', targetTest.hashid), {
          from_tab: currentTab
        }, {
          onSuccess: (page) => {
            // Update the local test data to reflect sent_to_lab status immediately
            if (targetTest) {
              // Create a single timestamp for consistency
              const timestamp = new Date().toLocaleString();
              
              // Update the tests array (used by Records component)
              targetTest.sent_to_lab = true;
              targetTest.sent_to_lab_datetime = timestamp;
              
              // Emit event to parent component to update task.tests for Actions tab
              this.$emit('test-sent-to-lab', {
                testHashid: targetTest.hashid,
                sent_to_lab: true,
                sent_to_lab_datetime: timestamp
              });
            }
          },
          onError: (errors) => {
            console.error('Error sending to lab:', errors);
            if (this.$toast) {
              this.$toast.error('Failed to send order to lab. Please try again.');
            }
          }
        });
        
      } catch (error) {
        console.error('Error sending to lab:', error);
        
        if (this.$toast) {
          this.$toast.error('Failed to send order to lab. Please try again.');
        }
      } finally {
        this.isProcessingLabOrder = false;
      }
    },

    dismissLabOrderBanner() {
      this.labOrderBannerDismissed = true;
      this.showLabOrderBannerAfterSave = false;
      
      if (this.$toast) {
        this.$toast.info('Lab order reminder dismissed. You can send orders from the Actions tab.');
      }
    },

    getLabTestDisplayName(code) {
      if (!code || !this.transformedLabTests) {
        return code || '';
      }
      return this.transformedLabTests[code] || code;
    },

    getParcelName(sample) {
      // Try multiple possible locations for parcel name
      if (sample.parcel && sample.parcel.name) {
        return sample.parcel.name;
      }
      if (sample.farm_location && sample.farm_location.parcel_name) {
        return sample.farm_location.parcel_name;
      }
      if (sample.parcel_name) {
        return sample.parcel_name;
      }
      // Try zone name as fallback since zones are now 1:1 with parcels
      if (sample.zone_name) {
        return sample.zone_name;
      }
      if (sample.sampleZone && sample.sampleZone.name) {
        return sample.sampleZone.name;
      }
      return null;
    },

    getParcelNumber(sample) {
      // Try multiple possible locations for parcel number/field_no
      if (sample.parcel && sample.parcel.field_no) {
        // If there's a suffix, combine them
        const suffix = sample.parcel.field_no_suffix ? sample.parcel.field_no_suffix : '';
        return sample.parcel.field_no + suffix;
      }
      if (sample.farm_location && sample.farm_location.field_no) {
        const suffix = sample.farm_location.field_no_suffix ? sample.farm_location.field_no_suffix : '';
        return sample.farm_location.field_no + suffix;
      }
      if (sample.field_no) {
        const suffix = sample.field_no_suffix ? sample.field_no_suffix : '';
        return sample.field_no + suffix;
      }
      // Try zone field_no as fallback since zones are now 1:1 with parcels
      if (sample.zone_field_no) {
        return sample.zone_field_no;
      }
      if (sample.sampleZone && sample.sampleZone.field_no) {
        const suffix = sample.sampleZone.field_no_suffix ? sample.sampleZone.field_no_suffix : '';
        return sample.sampleZone.field_no + suffix;
      }
      return null;
    },

    getLabTestCodeFromName(name) {
      if (!name || !this.transformedLabTests) {
        return name || '';
      }
      // Find the code that maps to this name
      for (const [code, testName] of Object.entries(this.transformedLabTests)) {
        if (testName === name) {
          return code;
        }
      }
      return name; // Return original if not found
    },

    getLabTestsDisplay(sample) {
      if (!sample.lab_tests) {
        return '';
      }
      
      if (Array.isArray(sample.lab_tests)) {
        return sample.lab_tests.join(', ');
      }
      
      return sample.lab_tests;
    },

    getStatusTooltip(sample) {
      if (sample.zone_issues) {
        return 'Warning: Multiple samples assigned to the same parcel/zone';
      }
      
      if (this.task.organisation_name === 'ISEC' && sample.lpis_issue) {
        return 'Error: LPIS validation issue';
      }
      
      if (!sample.matched) {
        return 'Sample not assigned to a parcel';
      }
      
      return 'Sample properly assigned';
    },



    sortBy(field) {
      if (this.sortField === field) {
        // Toggle direction if clicking the same field
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // Set new field and default to ascending
        this.sortField = field;
        this.sortDirection = 'asc';
      }
    },

    setSortOption(field, direction) {
      this.sortField = field;
      this.sortDirection = direction;
      this.showSortOptions = false;
    },

    setSortOption(field, direction) {
      this.sortField = field;
      this.sortDirection = direction;
      this.showSortOptions = false;
    },

    getCollectedDateValue(sample) {
      // For SMAP samples, prioritize sample_date_end
      if (sample.sample_date_end) {
        return new Date(sample.sample_date_end).getTime();
      }
      
      // For manual samples or SMAP samples without sample_date_end, use created_at
      if (sample.created_at) {
        return new Date(sample.created_at).getTime();
      }
      
      // Fallback to current time if no date available
      return new Date().getTime();
    },

    formatCollectedDate(sample) {
      let dateToFormat = null;
      
      // Prioritize sample_date_end for SMAP samples
      if (sample.sample_date_end) {
        dateToFormat = sample.sample_date_end;
      } else if (sample.created_at) {
        dateToFormat = sample.created_at;
      }
      
      if (!dateToFormat) {
        return 'Not set';
      }
      
      try {
        const date = new Date(dateToFormat);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return 'Invalid date';
      }
    },

    getCollectedDateTooltip(sample) {
      if (sample.sample_date_end) {
        return 'SMAP sample end time: ' + this.formatCollectedDate(sample);
      } else if (sample.created_at) {
        return 'Manual sample created time: ' + this.formatCollectedDate(sample);
      }
      return 'No collection date available';
    },

    toggleSampleDropdown(sampleHashid) {
      // Close all other dropdowns first
      this.openDropdowns = {};
      // Toggle the clicked dropdown
      this.openDropdowns[sampleHashid] = !this.openDropdowns[sampleHashid];
    },

    closeSampleDropdown(sampleHashid) {
      // Use setTimeout to allow click events to fire before closing
      setTimeout(() => {
        this.openDropdowns[sampleHashid] = false;
      }, 150);
    },

    getGeoreferenceCount(sample) {
      // For manual samples with georeferences, check manual_georeferences
      if (sample.manual_georeferences && Array.isArray(sample.manual_georeferences) && sample.manual_georeferences.length > 0) {
        return sample.manual_georeferences.length;
      }
      // For SMAP samples with markers (newer format)
      if (sample.markers && Array.isArray(sample.markers) && sample.markers.length > 0 && sample.paths_lat_lon) {
        const paths = sample.paths_lat_lon[0] || [];
        let validMarkerCount = 0;

        // Count only markers that have valid coordinates
        sample.markers.forEach(markerIndex => {
          // Handle case where markerIndex might be an array with a single value
          const actualIndex = Array.isArray(markerIndex) ? markerIndex[0] : markerIndex;
          
          if (paths[actualIndex] && Array.isArray(paths[actualIndex]) && paths[actualIndex].length >= 2) {
            validMarkerCount++;
          }
        });

        return validMarkerCount;
      }
      // For older SMAP samples without markers (backwards compatibility)
      // Georeference points became a legal requirement from September 15th, 2025
      // For samples collected on/after this date without markers, we use the mid-point (count as 1)  
      // For samples collected before this date, no georeference requirement existed (count as 0)
      else if (sample.paths_lat_lon && Array.isArray(sample.paths_lat_lon) && sample.paths_lat_lon.length > 0) {
        const sampleDate = sample.sample_date_end ? new Date(sample.sample_date_end) : null;
        const georeferenceRequirementDate = new Date('2025-09-15');
        
        if (sampleDate && sampleDate >= georeferenceRequirementDate) {
          return 1; // Mid-point is used as georeference for legal compliance
        }
      }
      
      return 0; // No georeference requirement for samples before September 15th, 2025
    },

    // Get georeference coordinates for display
    getGeoreferenceCoordinates(sample) {
      const coordinates = [];
      
      // For manual samples with georeferences, check manual_georeferences
      if (sample.manual_georeferences && Array.isArray(sample.manual_georeferences) && sample.manual_georeferences.length > 0) {
        sample.manual_georeferences.forEach((ref, index) => {
          if (Array.isArray(ref) && ref.length === 2) {
            coordinates.push({
              index: index + 1,
              lat: this.formatCoordinate(ref[0]),
              lng: this.formatCoordinate(ref[1]),
              type: 'Manual'
            });
          } else if (ref.lat !== undefined && (ref.lng !== undefined || ref.lon !== undefined)) {
            coordinates.push({
              index: index + 1,
              lat: this.formatCoordinate(ref.lat || ref[0]),
              lng: this.formatCoordinate(ref.lng || ref.lon || ref[1]),
              type: 'Manual'
            });
          }
        });
        return coordinates;
      }
      
      // For SMAP samples with markers (newer format)
      if (sample.markers && Array.isArray(sample.markers) && sample.markers.length > 0 && sample.paths_lat_lon) {
        const paths = sample.paths_lat_lon[0] || [];
        
        sample.markers.forEach((markerIndex, index) => {
          // Handle case where markerIndex might be an array with a single value
          const actualIndex = Array.isArray(markerIndex) ? markerIndex[0] : markerIndex;
          
          if (paths[actualIndex] && Array.isArray(paths[actualIndex]) && paths[actualIndex].length >= 2) {
            const [lng, lat] = paths[actualIndex]; // SMAP stores as [lng, lat]
            coordinates.push({
              index: index + 1,
              lat: this.formatCoordinate(lat),
              lng: this.formatCoordinate(lng),
              type: 'GPS'
            });
          }
        });
        return coordinates;
      }
      
      // For older SMAP samples without markers (backwards compatibility)
      // For samples collected on/after Sept 15, 2025, show the mid-point coordinate for legal compliance
      else if (sample.paths_lat_lon && Array.isArray(sample.paths_lat_lon) && sample.paths_lat_lon.length > 0) {
        const paths = sample.paths_lat_lon[0] || [];
        
        // Check if this sample is subject to georeference requirements
        const sampleDate = sample.sample_date_end ? new Date(sample.sample_date_end) : null;
        const georeferenceRequirementDate = new Date('2025-09-15');
        
        if (sampleDate && sampleDate >= georeferenceRequirementDate && paths.length > 0) {
          // Use the mid-point of the path as the georeference for legal compliance
          const midIndex = Math.floor(paths.length / 2);
          const coord = paths[midIndex];
          if (Array.isArray(coord) && coord.length >= 2) {
            const [lng, lat] = coord; // SMAP stores as [lng, lat]
            if (typeof lng === 'number' && typeof lat === 'number') {
              coordinates.push({
                index: 1,
                lat: this.formatCoordinate(lat),
                lng: this.formatCoordinate(lng),
                type: 'GPS'
              });
            }
          }
        }
        // For samples before Sept 15, 2025, don't show coordinates as there was no requirement
      }
      
      return coordinates;
    },

    // Format coordinate to 6 decimal places (following FarmMap.vue pattern)
    formatCoordinate(value) {
      return typeof value === 'number' ? value.toFixed(6) : value;
    },

    // Show coordinates modal
    showCoordinatesForSample(sample) {
      this.selectedSampleForCoordinates = sample;
      this.showCoordinatesModal = true;
    },

    // Close coordinates modal
    closeCoordinatesModal() {
      this.showCoordinatesModal = false;
      this.selectedSampleForCoordinates = null;
    },

    // Copy single coordinate to clipboard
    async copyCoordinate(coordinate) {
      const coordText = `${coordinate.lat}, ${coordinate.lng}`;
      try {
        await navigator.clipboard.writeText(coordText);
        if (this.$toast) {
          this.$toast.success(`Coordinate copied: ${coordText}`);
        }
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = coordText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        if (this.$toast) {
          this.$toast.success(`Coordinate copied: ${coordText}`);
        }
      }
    },

    // Copy all coordinates as table format
    async copyAllCoordinates() {
      if (!this.selectedSampleForCoordinates) return;
      
      const coordinates = this.getGeoreferenceCoordinates(this.selectedSampleForCoordinates);
      if (coordinates.length === 0) return;
      
      // Create table format with headers
      let tableText = 'Point\tLatitude\tLongitude\tType\n';
      coordinates.forEach(coord => {
        tableText += `${coord.index}\t${coord.lat}\t${coord.lng}\t${coord.type}\n`;
      });
      
      try {
        await navigator.clipboard.writeText(tableText);
        if (this.$toast) {
          this.$toast.success(`All ${coordinates.length} coordinates copied as table`);
        }
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = tableText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        if (this.$toast) {
          this.$toast.success(`All ${coordinates.length} coordinates copied as table`);
        }
      }
    },

    // Handle match unassigned action
    matchUnassigned() {
      this.showActionsMenu = false;
      this.showAddSubmenu = false;
      this.showAssignSubmenu = false;
      this.goTo(route('app.task.match-unassigned', this.task.id));
    },

    // Handle rematch all action
    rematchAll() {
      this.showActionsMenu = false;
      this.showAddSubmenu = false;
      this.showAssignSubmenu = false;
      this.goTo(route('app.task.rematch-all', this.task.id));
    },

    confirmDelete(sample) {
      const vm = this;
      this.$swal
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        })
        .then((result) => {
          if (result.isConfirmed) {
            vm.$inertia.delete(route("app.task.delete-record", { sample }));
          }
        });
    },

    // Handle document clicks to close dropdowns
    handleDocumentClick(e) {
      if (!e.target.closest('.relative')) {
        this.showActionsMenu = false;
        this.showAddSubmenu = false;
        this.showAssignSubmenu = false;
      }
    },

    // Comment modal methods
    openCommentModal(index) {
      this.editingSampleIndex = index;
      this.tempCommentValue = this.newSamples[index].comment || '';
      this.showCommentModal = true;
    },

    closeCommentModal() {
      this.showCommentModal = false;
      this.editingSampleIndex = null;
      this.tempCommentValue = '';
    },

    saveComment() {
      if (this.editingSampleIndex !== null) {
        this.newSamples[this.editingSampleIndex].comment = this.tempCommentValue.trim();
      }
      this.closeCommentModal();
    },

    // LPIS modal methods
    openLpisModal(index) {
      this.editingSampleIndex = index;
      this.tempLpisValue = this.newSamples[index].lpis || '';
      this.showLpisModal = true;
    },

    closeLpisModal() {
      this.showLpisModal = false;
      this.editingSampleIndex = null;
      this.tempLpisValue = '';
    },

    saveLpis() {
      if (this.editingSampleIndex !== null) {
        this.newSamples[this.editingSampleIndex].lpis = this.tempLpisValue.trim();
      }
      this.closeLpisModal();
    },

    // Restore original column visibility
    restoreOriginalColumnVisibility() {
      if (this.originalColumnVisibility && this.columnVisibility) {
        this.columnVisibility.comment = this.originalColumnVisibility.comment;
        this.columnVisibility.lpis = this.originalColumnVisibility.lpis;
        this.saveColumnVisibility();
        this.originalColumnVisibility = null;
      }
    },

    // Marker editing methods
    isSMAPSample(sample) {
      return sample.paths_lat_lon && Array.isArray(sample.paths_lat_lon) && sample.paths_lat_lon.length > 0;
    },

    openMarkerEditModal(sample) {
      // Activate marker editing mode (similar to manual georeference selection)
      this.isEditingMarkers = true;
      this.editingMarkerSample = sample;
      this.tempMarkerIndices = sample.markers ? [...sample.markers] : [];
      
      // Show the map if on mobile
      if (this.$root.showMapOnMobile !== undefined) {
        this.$root.showMapOnMobile = true;
      }
      
      // Emit event to enable marker selection mode on the map
      this.$emitter.emit('startGPSMarkerSelection', {
        action: 'start-gps-marker-selection',
        sampleId: sample.sample_id || sample.id,
        pathCoordinates: sample.paths_lat_lon,
        currentMarkers: sample.markers || []
      });
      
      if (this.$toast) {
        this.$toast.info(`MARKER SELECTION MODE
Click GPS path points on the map to select/deselect markers for ${sample.sample_id}.
Click "Finish" to save your selection.`);
      }
    },

    closeMarkerEditModal() {
      // Exit marker editing mode (similar to manual georeference selection)
      this.isEditingMarkers = false;
      this.editingMarkerSample = null;
      this.tempMarkerIndices = [];
      this.isUpdatingMarkers = false;
      
      // Hide mobile map
      if (this.$root.showMapOnMobile !== undefined) {
        this.$root.showMapOnMobile = false;
      }
      
      // Emit event to stop marker selection mode
      this.$emitter.emit('stopGPSMarkerSelection', {
        action: 'stop-gps-marker-selection'
      });
    },

    getPathCoordinates(sample) {
      if (!sample.paths_lat_lon || !sample.paths_lat_lon[0]) return [];
      const paths = sample.paths_lat_lon[0];
      return paths.map((coord, index) => {
        const [lng, lat] = coord;
        return {
          index,
          lat: this.formatCoordinate(lat),
          lng: this.formatCoordinate(lng)
        };
      });
    },

    isCurrentMarker(sample, index) {
      if (!sample.markers || !Array.isArray(sample.markers)) return false;
      return sample.markers.includes(index);
    },

    selectAllMarkers() {
      if (!this.editingMarkerSample) return;
      const pathCount = this.editingMarkerSample.paths_lat_lon[0]?.length || 0;
      this.tempMarkerIndices = Array.from({length: pathCount}, (_, i) => i);
    },

    clearAllMarkers() {
      this.tempMarkerIndices = [];
    },

    async saveMarkerChanges() {
      if (!this.editingMarkerSample || this.isUpdatingMarkers) return;
      
      this.isUpdatingMarkers = true;
      
      try {
        const response = await axios.patch(
          route('app.task.update-sample-markers', this.editingMarkerSample.hashid),
          {
            markers: this.tempMarkerIndices
          }
        );

        // Update the sample in place
        this.editingMarkerSample.markers = [...this.tempMarkerIndices];
        
        // Refresh samples data to ensure consistency
        this.$inertia.reload({ 
          only: ['samples', 'labUpdateChanges', 'labUpdateNeeded'],
          preserveScroll: true,
          onFinish: () => {
            // After Inertia finishes reloading, update the global paths state that the map uses
            // This ensures the map gets the updated marker data
            this.$nextTick(() => {
              // Access the updated samples from the parent component (Task/View) via $parent
              const updatedSamples = this.$parent.samples || this.samples;
              if (updatedSamples && this.$root.paths) {
                // Process samples to include georeference marker coordinates (same as Task/View.vue)
                const samplesWithMarkers = updatedSamples.map(sample => ({
                  ...sample,
                  marker_coordinates: this.$parent.extractMarkerCoordinates ? 
                    this.$parent.extractMarkerCoordinates(sample) : 
                    this.extractMarkerCoordinates(sample)
                }));
                
                this.$root.paths.samples = samplesWithMarkers;
              }
            });
          }
        });
        
        this.closeMarkerEditModal();
        
        if (this.$toast) {
          const count = this.tempMarkerIndices.length;
          this.$toast.success(`Updated markers: ${count} point${count !== 1 ? 's' : ''} selected as georeferences.`);
        }
      } catch (error) {
        console.error('Error updating markers:', error);
        if (this.$toast) {
          const message = error.response?.data?.message || 'Failed to update markers. Please try again.';
          this.$toast.error(message);
        }
      } finally {
        this.isUpdatingMarkers = false;
      }
    },

    // GPS marker selection methods (similar to manual georeference selection)
    finishMarkerSelection() {
      // Save marker selection and exit editing mode
      this.saveMarkerChanges();
    },

    cancelMarkerSelection() {
      // Cancel marker editing and restore original selection
      if (this.$toast) {
        this.$toast.info('Marker selection cancelled');
      }
      this.closeMarkerEditModal();
    },

    handleGPSMarkerClick(payload) {
      // Handle clicks on GPS path points during marker selection mode
      if (!this.isEditingMarkers || !this.editingMarkerSample) return;
      
      const pointIndex = payload.pointIndex;
      const isCurrentlySelected = this.tempMarkerIndices.includes(pointIndex);
      
      if (isCurrentlySelected) {
        // Remove from selection
        this.tempMarkerIndices = this.tempMarkerIndices.filter(index => index !== pointIndex);
        if (this.$toast) {
          this.$toast.info(`Point ${pointIndex + 1} deselected`);
        }
      } else {
        // Add to selection
        this.tempMarkerIndices.push(pointIndex);
        if (this.$toast) {
          this.$toast.success(`Point ${pointIndex + 1} selected`);
        }
      }
      
      // Update map visual feedback for the clicked point
      this.$emitter.emit('updateGPSMarkerSelection', {
        action: 'update-gps-marker-selection',
        sampleId: this.editingMarkerSample.sample_id || this.editingMarkerSample.id,
        selectedMarkers: this.tempMarkerIndices,
        clickedPointIndex: pointIndex
      });
    },

    // Interactive marker map methods
    async initializeMarkerMap() {
      if (!this.editingMarkerSample || !this.editingMarkerSample.paths_lat_lon) return;
      
      // Check if ArcGIS modules are available
      if (!Map || !MapView || !GraphicsLayer) {
        console.error('ArcGIS modules not available');
        if (this.$toast) {
          this.$toast.error('Map functionality not available. Please refresh the page.');
        }
        return;
      }
      
      this.isLoadingMarkerMap = true;
      
      try {
        // Clean up existing map first
        this.cleanupMarkerMap();
        
        // Create map instance
        const map = new Map({
          basemap: "satellite"
        });
        
        // Create graphics layer for interactive elements
        this.markerMapGraphicsLayer = new GraphicsLayer();
        map.add(this.markerMapGraphicsLayer);
        
        // Initialize map view
        this.markerMapView = new MapView({
          container: "markerMapContainer",
          map: map,
          zoom: 16
        });
        
        await this.markerMapView.when();
        
        // Render GPS path and interactive points
        this.renderGPSPath();
        this.renderInteractivePoints();
        
        // Set up click event handlers
        this.setupMapClickHandlers();
        
      } catch (error) {
        console.error('Error initializing marker map:', error);
        if (this.$toast) {
          this.$toast.error('Failed to load interactive map. Please try again.');
        }
      } finally {
        this.isLoadingMarkerMap = false;
      }
    },
    
    renderGPSPath() {
      if (!this.editingMarkerSample || !this.markerMapGraphicsLayer) return;
      
      const paths = this.editingMarkerSample.paths_lat_lon[0];
      if (!paths || paths.length === 0) return;
      
      // Convert coordinates and create polyline
      const pathCoords = paths.map(coord => [coord[0], coord[1]]); // [lng, lat]
      
      const polyline = new Polyline({
        paths: [pathCoords],
        spatialReference: { wkid: 4326 }
      });
      
      const pathSymbol = new SimpleLineSymbol({
        color: [59, 130, 246], // Blue color for GPS path
        width: 3,
        style: "solid"
      });
      
      const pathGraphic = new Graphic({
        geometry: polyline,
        symbol: pathSymbol,
        attributes: { type: "gps-path" }
      });
      
      this.markerMapGraphicsLayer.add(pathGraphic);
      
      // Zoom to the path extent
      this.markerMapView.goTo(pathGraphic.geometry);
    },
    
    renderInteractivePoints() {
      if (!this.editingMarkerSample || !this.markerMapGraphicsLayer) return;
      
      const paths = this.editingMarkerSample.paths_lat_lon[0];
      if (!paths || paths.length === 0) return;
      
      this.markerPointGraphics = [];
      
      paths.forEach((coord, index) => {
        const [lng, lat] = coord;
        
        const point = new Point({
          longitude: lng,
          latitude: lat,
          spatialReference: { wkid: 4326 }
        });
        
        // Determine if this point is currently selected as a marker
        const isSelected = this.tempMarkerIndices.includes(index);
        
        const pointSymbol = new SimpleMarkerSymbol({
          color: isSelected ? [34, 197, 94] : [156, 163, 175], // Green if selected, gray if not
          size: isSelected ? 12 : 8,
          style: "circle",
          outline: {
            color: "white",
            width: 2
          }
        });
        
        const pointGraphic = new Graphic({
          geometry: point,
          symbol: pointSymbol,
          attributes: {
            type: "interactive-point",
            pointIndex: index,
            isSelected: isSelected,
            lat: lat,
            lng: lng
          }
        });
        
        this.markerMapGraphicsLayer.add(pointGraphic);
        this.markerPointGraphics.push(pointGraphic);
      });
    },
    
    setupMapClickHandlers() {
      if (!this.markerMapView) return;
      
      this.markerMapView.on("click", (event) => {
        this.markerMapView.hitTest(event).then((response) => {
          if (response.results.length > 0) {
            const firstResult = response.results[0];
            const graphic = firstResult.graphic;
            
            if (graphic.attributes && graphic.attributes.type === "interactive-point") {
              this.toggleMarkerPoint(graphic);
            }
          }
        });
      });
    },
    
    toggleMarkerPoint(pointGraphic) {
      const pointIndex = pointGraphic.attributes.pointIndex;
      const isCurrentlySelected = this.tempMarkerIndices.includes(pointIndex);
      
      if (isCurrentlySelected) {
        // Remove from selection
        this.tempMarkerIndices = this.tempMarkerIndices.filter(index => index !== pointIndex);
      } else {
        // Add to selection
        this.tempMarkerIndices.push(pointIndex);
      }
      
      // Update visual appearance
      this.updatePointAppearance(pointGraphic, !isCurrentlySelected);
    },
    
    updatePointAppearance(pointGraphic, isSelected) {
      const newSymbol = new SimpleMarkerSymbol({
        color: isSelected ? [34, 197, 94] : [156, 163, 175], // Green if selected, gray if not
        size: isSelected ? 12 : 8,
        style: "circle",
        outline: {
          color: "white",
          width: 2
        }
      });
      
      pointGraphic.symbol = newSymbol;
      pointGraphic.attributes.isSelected = isSelected;
    },
    
    cleanupMarkerMap() {
      if (this.markerMapView) {
        this.markerMapView.destroy();
        this.markerMapView = null;
      }
      
      this.markerMapGraphicsLayer = null;
      this.markerPointGraphics = [];
      this.isLoadingMarkerMap = false;
    },
    
    // Enhanced marker selection methods for map interface
    selectAllMarkersOnMap() {
      if (!this.editingMarkerSample) return;
      const pathCount = this.editingMarkerSample.paths_lat_lon[0]?.length || 0;
      this.tempMarkerIndices = Array.from({length: pathCount}, (_, i) => i);
      
      // Update visual appearance of all points
      this.markerPointGraphics.forEach((graphic, index) => {
        this.updatePointAppearance(graphic, true);
      });
    },
    
    clearAllMarkersOnMap() {
      this.tempMarkerIndices = [];
      
      // Update visual appearance of all points
      this.markerPointGraphics.forEach((graphic) => {
        this.updatePointAppearance(graphic, false);
      });
    },
    
    // Fallback methods for compatibility (if duplicates exist)
    selectAllMarkers() {
      // Delegate to map-aware method
      this.selectAllMarkersOnMap();
    },
    
    clearAllMarkers() {
      // Delegate to map-aware method
      this.clearAllMarkersOnMap();
    },

    extractMarkerCoordinates(sample) {
      if (!sample.markers || !Array.isArray(sample.markers) || !sample.paths_lat_lon) {
        return [];
      }

      const paths = sample.paths_lat_lon[0] || [];
      const markerCoordinates = [];

      // Extract coordinates for each marker index
      sample.markers.forEach(markerIndex => {
        // Handle case where markerIndex might be an array with a single value
        const actualIndex = Array.isArray(markerIndex) ? markerIndex[0] : markerIndex;
        
        if (paths[actualIndex] && Array.isArray(paths[actualIndex]) && paths[actualIndex].length >= 2) {
          markerCoordinates.push({
            longitude: paths[actualIndex][0],
            latitude: paths[actualIndex][1],
            index: actualIndex,
            sample_id: sample.sample_id || sample.id
          });
        }
      });

      return markerCoordinates;
    },

    handleSampleIdSearch(value) {
      this.sampleIdSearch = value;
    },
  },
};
</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style scoped>
/* Simple styling for better spacing */
.flex-1 {
  flex: 1;
}

.flex-shrink-0 {
  flex-shrink: 0;
}
</style>
